import React, { ReactElement, useState, useContext, useEffect } from "react";
import MuiDrawer from "@mui/material/Drawer";
import { authContext } from "../Context/Authentication/authContext";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import {
  FormControl,
  InputLabel,
  MenuItem,
  ListItemText,
  Checkbox,
  RadioGroup,
  FormControlLabel,
  Slider,
  Chip,
  Typography,
  Box,
  Radio,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Grid,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Autocomplete from "@mui/material/Autocomplete";
import { useDebounce } from "./useDebounce";
import { baseURL } from "../config";
import {
  generateDropDownRanges,
  getCityByState,
  getCountyByState,
  getMetroByState,
} from "../Helpers/helpers";
import axios from "axios";
import "./SideMenu.scss";
import { getMortgageRate } from "../WhatIfAnalysis/helper";
import moment from "moment";
import { TrackingEvents, useAnalytics } from "../Context/Analytics.context";

const HEATMAP_MONTH_OPTIONS = [
  "6",
  "12",
  "18",
  "24",
  "30",
  "36",
  "42",
  "48",
  "54",
];
const HEATMAP_MILES_OPTIONS = ["150 Mile", "200 Mile", "250 Mile", "300 Mile"];
const drawerWidth = 336;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const iOSBoxShadow =
  "0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.13),0 0 0 1px rgba(0,0,0,0.02)";

const IOSSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "#f7b047" : "#f7b047",
  height: 2,
  padding: "15px 0",
  "& .MuiSlider-thumb": {
    height: 15,
    width: 15,
    backgroundColor: "#f7b047",
    boxShadow: iOSBoxShadow,
    "&:focus, &:hover, &.Mui-active": {
      boxShadow:
        "0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)",
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        boxShadow: iOSBoxShadow,
      },
    },
  },
  "& .MuiSlider-valueLabel": {
    fontSize: 12,
    fontWeight: "normal",
    top: -6,
    backgroundColor: "unset",
    color: theme.palette.text.primary,
    "&:before": {
      display: "none",
    },
    "& *": {
      background: "transparent",
      color: theme.palette.mode === "dark" ? "#fff" : "#f7b047",
    },
  },
  "& .MuiSlider-track": {
    color: "#f7b047",
  },
  "& .MuiSlider-rail": {
    opacity: 0.4,
    border: "1px solid #bfbfbf",
    backgroundColor: "#bfbfbf",
  },
  "& .MuiSlider-mark": {
    backgroundColor: "#bfbfbf",
    height: 8,
    width: 1,
    "&.MuiSlider-markActive": {
      opacity: 1,
      color: "rgb(255, 75, 75)",
      backgroundColor: "currentColor",
    },
  },
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  background: "rgb(240, 242, 246)",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  background: "rgb(240, 242, 246)",
  overflowX: "hidden",
  width: `calc(${theme.spacing(0)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(0)} + 1px)`,
  },
});

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
    borderBottom: "1px solid #f7b047",
    borderTop: "1px solid #f7b047",
    maxWidth: "1000px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiDialogTitle-root": {
    color: "#f7b047",
  },
  "& .MuiSvgIcon-root": {
    color: "#f7b047",
  },
  "& .MuiButton-root": {
    color: "#f7b047",
  },
  "& .MuiPaper-root": {
    maxWidth: "1000px",
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

const BootstrapDialogTitle = (props: DialogTitleProps) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(!open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const getYears = () => {
  let temp = [];
  for (let i = 2011; i < 2029; i++) {
    temp.push({
      value: i,
      label: i,
    });
  }
  return temp;
};


const returnHeatMapText = () => {
  return (
    <div className="heatmap-subtext">
      <span className="heatmap-subtext-heading">Expected Returns:</span>
      <br />
      1.
      The map highlights the expected market volume (total anticipated home sales revenue) for each ZIP
      code over a 3-month period after the selected <span style={{ marginLeft: '16px' }}>quarter.</span>
      <br />
      2.It's the estimated profit
      we expect to make from sales in that area—within 3 months after the
      selected timeframe..
      <br />
      3.We estimate profit based on expected median price per square foot and local interest rates to identify the most profitable sales locations.
      <br />
      4. Green shows positive returns and red shows negative returns.
      <br />
      5. Darker green shows high returns and darker red shows high negative
      returns.
      <br />
      <br />
      <span className="heatmap-subtext-heading">Days on Market:</span>
      <br />
      1. The map shows median days on market for a home sale for each zip code
      for selected time.
      <br />
      2. Darker green shows high cumulative home sale.
      <br />
      <br />
      <span className="heatmap-subtext-heading">Home Sales:</span>
      <br />
      1. The map shows total cumulative home sale for each zip code for selected
      time.
      <br />
      2. Darker green shows high cumulative home sale.
      <br />
      <br />
      <span className="heatmap-subtext-heading">PPSF Growth:</span>
      <br />
      1. The map shows median price for square foot for a home sale for each zip
      code for selected time.
      <br />
      2. Green shows that growth is positive and red shows growth is negative.
      <br />
      3. Darker green shows high growth (positive) and darker red shows low
      growth (negative).
      <br />
      <br />
      <span className="heatmap-subtext-heading">Days on Market Change:</span>
      <br />
      1. The map shows percentage change in median days on market for each zip
      code for selected time.
      <br />
      2. Green shows that growth is negative and red shows growth is positive.
      <br />
      3. Darker green shows low growth (negative) and darker red shows high
      growth (positive).
      <br />
      <br />
      <span className="heatmap-subtext-heading">Home Sale Growth:</span>
      <br />
      1. The map shows total cumulative home sale growth in comparison with same
      historical period for each zip code for selected time.
      <br />
      2. Green shows that growth is positive and red shows growth is negative.
      <br />
      3. Darker green shows high growth (positive) and darker red shows low
      growth (negative).
      <br />
      <br />
    </div>
  );
};

const returnWhatIfCriteriaText = () => {
  return (
    <div className="heatmap-subtext">
      1. Time Series Components plots show the components Trend, Seasonality and
      Residue of each observed historical value. It is assumed that the change
      in component residue comes from changes in economic trends and other
      infrastructure facilities of those cities.
      <br />
      <br />
      2. Adjusted plots show 'to be adjusted values' for model predictions if
      same points of increase or decrease in mortgage rate, unemployment rate
      and population growth percentage as chosen for the current month continue
      for future months.
      <br />
      <br />
      3. Residue Percentage Vs Mortgage rate, Unemployment rate and Population
      growth percentage charts show the histirical comparison of each of PPSF,
      Home Sales and Days on Market residue percentages on trend and seasonality
      vs Mortgage rate, Unemployment rate and Population growth percentage.
      <br />
      <br />
    </div>
  );
};

const ZIPCODE_DROPDOWN = ["Home sales", "Days on market", "Price", "MSP"];
const PERCENTAGE_DROPDOWN = ["Price", "MSP"];
const VALUE_DROPDOWN = ["Home sales", "Days on market", "Price", "MSP",'Months of Supply'];
const QUARTER_DROPDOWN = ["Prediction"];
const DEMOGRAPHIC_DROPDOWN = ["Demographics"];
const CRIME_DROPDOWN = ["Crime"];
const RENTAL_DROPDOWN = ["Rentals"];
const SLIDER_YEAR_RANGE = [
  "Home sales",
  "Days on market",
  "Price",
  "MSP",
  "Population",
  "Permits",
  "Rentals",
  "Travel Time",
  "Crime",
  "Months of Supply",
];
const SLIDER_COUNT_RANGE = ["Prediction"];
const SHOW_INTERVALS = ["Home sales", "Days on market", "Price", "MSP",'Months of Supply'];
const SLIDER_START_YEAR = 2011;
const SLIDER_END_YEAR = parseInt(moment().format("YYYY")) + 6;
const SLIDER_DEFAULT_VALUE = [2018, 2028];
const ADJUSTED_PREDICTIONS = ["Home sales", "Days on market", "Price", "MSP"];
const NO_OF_QUARTERS = Array.from({ length: 20 }, (_, index) => index + 1);
const DISABLE_MULTI_SELECT = ["Demographics", "Property Type", "Prediction"];
const DISABLE_MSA_MULTI_SELECT = [
  "Demographics",
  "Property Type",
  "Prediction",
  "Home sales",
];
const PROPERTY_TYP_TAB = ["Property Type"];
const predictionTypes = [
  "Mortgage Rates",
  "Consumer Sentiments(HPSI)",
  "Builder Sentiments(NAHB)",
];
const zstatesList = [
  "Alabama",
  "Arkansas",
  "Connecticut",
  "Delaware",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maryland",
  "Michigan",
  "Mississippi",
  "Missouri",
  "Ohio",
  "Pennsylvania",
  "West Virginia",
  "Nebraska",
  "Vermont",
  "Massachusetts",
  "New Mexico",
  "Hawaii",
  "South Dakota",
  "Alaska",
  "Rhode Island",
  "New Hampshire",
  "Nevada",
  "New Jersey",
  "New York",
  "Oklahoma",
];

const marks = [
  { value: 0 },
  { value: 1 },
  { value: 2 },
  { value: 3 },
  { value: 4 },
  { value: 5 },
  { value: 6 },
  { value: 7 },
  { value: 8 },
  { value: 9 },
  { value: 10 },
];

const QuarterSlider = styled(Slider)({
  color: "#fff",
  height: 250,
  "& .MuiSlider-track": {
    border: "none",
  },
  "& .MuiSlider-thumb": {
    // height: 24,
    // width: 24,
    backgroundColor: "#f7b047",
    border: "2px solid #f7b047",
    "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
      boxShadow: "inherit",
    },
    "&:before": {
      display: "none",
    },
  },
  "&.Mui-disabled .MuiSlider-thumb": {
    backgroundColor: "#a0a0a0",
    border: "2px solid #a0a0a0",
  },
  "& .MuiSlider-valueLabel": {
    lineHeight: 1.2,
    fontSize: 12,
    fontWeight: 600,
    padding: 0,
    width: 32,
    height: 32,
    borderRadius: "50% 50% 50% 0",
    background:
      "linear-gradient(#fab144 -49.88%, rgba(255, 168, 13, 0.1) 102.13%)",
    transformOrigin: "bottom left",
    transform: "translate(50%, -100%) rotate(-45deg) scale(0)",
    left: "calc(-50% + 4px)",
    "&:before": { display: "none" },
    "&.MuiSlider-valueLabelOpen": {
      transform: "translate(50%, -100%) rotate(-45deg) scale(1)",
    },
    "& > *": {
      transform: "rotate(45deg)",
    },
  },
  "& .MuiSlider-markLabel": {
    color: "#f7b047",
  },
});

const schoolLevelRating = [
  {
    id: 1,
    key: "All",
    value: "All",
  },
  {
    id: 2,
    key: "Below Average",
    value: "Below average",
  },
  {
    id: 3,
    key: "Above Average",
    value: "Above average",
  },
  {
    id: 4,
    key: "Average",
    value: "Average",
  },
];

const SideMenu = ({
  open,
  handleDrawerToggle,
  callback,
}: any): ReactElement => {
  const { dispatch, state }: any = useContext(authContext);
  const [cities, setCities]: any = React.useState([]);
  const [county, setCounty]: any = React.useState([]);
  const [msa, setMsa]: any = React.useState([]);
  const [zipCodes, setZipCodes] = React.useState<string[]>([]);
  const [zipCodesPredictions, setZipCodesPredictions] = React.useState<
    string[]
  >([]);
  const [schoolType, setSchoolType]: any = React.useState("Public");
  const [schoolRating, setSchoolRating]: any = React.useState("All");
  const [year, setYear] = React.useState<number[]>(SLIDER_DEFAULT_VALUE);
  const [qYears, setQYears]: any = React.useState([]);
  const [dates, setDates]: any = useState([]);
  const [prediction, setPrediction]: any = React.useState(0);
  const [predictionCount, setPredictionCount]: any = React.useState(1000);
  const [states, setStates]: any = React.useState([]);
  const debounceYear = useDebounce(year, 300);
  const debouncePredictions = useDebounce(prediction, 300);
  const [profitability, setProfitability]: any = React.useState("All Zipcodes");
  const [showMarkers, setShowMarkers] = React.useState(true);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [heatMapRecords, setHeatMapRecords]: any = React.useState(
    state?.filters?.totalHeatmapRecords || 500
  );
  const [showAdjustedPredictionPopUp, setShowAdjustedPredictionPopUp] =
    React.useState(false);
  const [mortgageRate, setMortgageRate] = useState(7.442);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [adjustedRateFor, setAdjustedRateFor]: any = React.useState("");
  const [prevString, setPrevString] = useState("");
  const [selectedPredictions, setSelectedPredictions]: any = React.useState([]);
  const { track } = useAnalytics();
  const [dataAvailability, setDataAvailability] = useState<any>({
    totalSales: false,
    reSales: false,
    newSales: false
  });

  useEffect(() => {
    track(TrackingEvents[state.filters.trackEvent], {
      state: state.filters.state,
      selectedType: state.filters.selectedType,
      selectedTab: state.selectedTab,
      city: state.filters.city,
      zipcode: state.filters.zipcode,
      value: state.filters.value,
      showInterval: state.filters.showInterval,
      demographicValue: state.filters.demographicValue,
      adjustedPrediction: {
        mortgageRate: state.filters.adjustedPredictions,
        consumer: state.filters.adjustedSentimentsByConsumer,
        builder: state.filters.adjustedSentimentsByBuilder,
      },
    });
  }, [
    state.filters.state,
    state.filters.selectedType,
    state.selectedTab,
    state.filters.city,
    state.filters.zipcode,
    state.filters.value,
    state.filters.showInterval,
    state.filters.adjustedPredictions,
    state.filters.adjustedSentimentsByConsumer,
    state.filters.adjustedSentimentsByBuilder,
  ]);
   useEffect(() => {
    if (state?.selectedTab === 'Home sales' && state?.filters?.msa?.length > 0) {
      checkDataAvailability();
    }
  }, [state?.selectedTab, state?.filters?.msa]);

  const checkDataAvailability = async () => {
    const msa = state.filters.msa.map((m: any) => m.name);
    const formattedArray = msa.map((item: any) => `'${item}'`).join(",");
    const start = state.filters.years[0];
    const end = state.filters.years[1];
    
    // Options to check
    const optionsToCheck = ['total_sales', 'resale', 'new_sale'];
    
    try {
      // Create an array of promises for each option
      const promises = optionsToCheck.map(async (option) => {
        let url = baseURL;
        url = `${url}getHomeSalesMetroCities?state=${
          state.filters.state
        }&metroarea=${formattedArray}&startYear=${start}&endYear=${end}&flag=${state.filters.value.toLowerCase()}&category=${option}`;
        
        try {
          const response = await axios.get(url);
          console.log(response.data.cube[msa].length)
          // Check if response has data (you may need to adjust this based on your API response structure)
          const hasData = response.data?.cube && response.data.cube[msa].length > 0;
          return {
            option,
            hasData
          };
        } catch (error) {
          console.error(`Error fetching data for ${option}:`, error);
          return {
            option,
            hasData: false
          };
        }
      });
      
      // Wait for all promises to resolve
      const results = await Promise.all(promises);
       setDataAvailability({
        totalSales: results.find(r => r.option === 'total_sales')?.hasData || false,
        reSales: results.find(r => r.option === 'resale')?.hasData || false,
        newSales: results.find(r => r.option === 'new_sale')?.hasData || false
      });
      
    } catch (error) {
      console.error("Error checking data availability:", error);
    } finally {
      
    }
  };
  const baseAdjustedPrediction = () => {
    const result: any = {};
    for (let i = 1; i <= 20; i++) {
      result[`quarter_${i}`] = 0;
    }
    return result;
  };

  const [confirmationValues, setConfirmationValues] = React.useState(
    baseAdjustedPrediction()
  );

  const mortgageRateCalculation = (mortByQuarters: any) => {
    console.log(mortgageRate);
    let rate =
      adjustedRateFor == "Mortgage Rates"
        ? mortgageRate
        : adjustedRateFor == "Consumer Sentiments(HPSI)"
        ? 65
        : 37;
    let nonZeroValues = Object.values(mortByQuarters);
    nonZeroValues = nonZeroValues.filter((value: any) => value > 0);

    if (mortByQuarters.quarter_1 === 0) {
      mortByQuarters.quarter_1 = rate;
    }

    if (nonZeroValues.length > 0 && mortByQuarters.quarter_20 === 0) {
      mortByQuarters.quarter_20 = nonZeroValues[nonZeroValues.length - 1];
    }

    if (nonZeroValues.length === 0) {
      mortByQuarters.quarter_1 = rate;
      mortByQuarters.quarter_20 = rate;
    }
    const nonZeroIndices: any = Object.entries(mortByQuarters).reduce(
      (acc: any, [key, value]) => {
        if (value !== 0) acc.push(parseInt(key.match(/\d+/)[0]));
        return acc;
      },
      []
    );

    for (let i = 0; i < nonZeroIndices.length - 1; i++) {
      const startIndex = nonZeroIndices[i];
      const endIndex = nonZeroIndices[i + 1];
      const startValue = mortByQuarters[`quarter_${startIndex}`];
      const endValue = mortByQuarters[`quarter_${endIndex}`];

      const linspaceValues = linspace(
        startValue,
        endValue,
        endIndex - startIndex + 1
      ).slice(1, -1);

      linspaceValues.forEach((value, j) => {
        mortByQuarters[`quarter_${startIndex + 1 + j}`] = parseFloat(
          value.toFixed(2)
        ); // Ensure consistent floating-point precision
      });
    }

    return mortByQuarters;
  };

  const linspace = (start: any, end: any, num: any) => {
    const step = (end - start) / (num - 1);
    return Array.from({ length: num }, (_, i) => start + step * i);
  };

  const handleClickOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    handleOpenAdjustedPredictionPopup(false);
    handleOpenAdjustedSentimentPopup(false);
    setShowConfirmation(false);
    // switch(adjustedRateFor){
    //   case '':

    // }
    console.log(adjustedRateFor, state.filters.adjustedPredictions);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        adjustedPredictions:
          adjustedRateFor == "Mortgage Rates"
            ? false
            : state.filters.adjustedPredictions,
        adjustedSentimentsByConsumer:
          adjustedRateFor == "Consumer Sentiments(HPSI)"
            ? false
            : state.filters.adjustedSentimentsByConsumer,
        adjustedSentimentsByBuilder:
          adjustedRateFor == "Builder Sentiments(NAHB)"
            ? false
            : state.filters.adjustedSentimentsByBuilder,
      },
    });
    // var rate = state.filters.adjustedPredictions ? 'Mortgage Rates': state.filters.adjustedSentimentsByConsumer ? 'Consumer Sentiments(HPSI)': state.filters.adjustedSentimentsByBuilder ? 'Builder Sentiments(NAHB)' : ''
    // console.log(rate)
    setAdjustedRateFor(prevString);
  };

  const handleHeatMapRecordsChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    const val = newValue as number;
    setHeatMapRecords(val);
  };

  const handleHeatMapRecordsChangeCommitted = (
    event: React.SyntheticEvent | Event,
    newValue: number | Array<number>
  ) => {
    const val = newValue as number;
    setHeatMapRecords(val);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        heatmapTopRecords: val,
        showHeatMap: true,
      },
    });
  };

  useEffect(() => {
    if (!state?.filters.states) {
      getStates();
    } else {
      setStates(
        state?.filters.states.sort((a: any, b: any) => {
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        })
      );
    }
  }, [state?.filters?.state]);

  useEffect(() => {
    getCitiesByState();
    getMortgageRateValue();
    if (state.filters.selectedType === "cityPredictions") {
      setZipCodesPredictions([]);
      getCityPredictionCount();
    } else if (state.filters.selectedType === "msaPredictions") {
      setZipCodesPredictions([]);
      getMsaCount();
    } 
    else if (state.filters.selectedType === "countyPredictions") {
      setZipCodesPredictions([]);
      getCountyPredictionCount();
    }else if (state.filters.selectedType === "zipcodePredictions") {
      setZipCodesPredictions([]);
      getZipcodePredictionCount();
    }
  }, [state?.filters?.state, state.filters.selectedType]);

  const getCountyPredictionCount = async () => {
    const { cityPredValues, predictionBoundary } =
      await setPredictionBoundary();
    const response = await axios.get(
      `${baseURL}getCountyPredictionSummary?state=${state?.filters?.state}&date=${cityPredValues[0]}`
    );
    const count = response.data?.cube?.length;
    setPredictionCount(count);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        noOfPrediction: count,
        predictionFor: cityPredValues[0],
        predictionTill: predictionBoundary,
      },
    });
  };

  const getMortgageRateValue = async () => {
    const mRate = await getMortgageRate();
    if (mRate !== 0) setMortgageRate(mRate);
  };

  useEffect(() => {
    if (state.filters.selectedType === "heatMap") {
      const selectedStateName = state?.filters.selectedTerritory || "Florida";
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          selectedTerritory: selectedStateName,
          profitability: "All Zipcodes",
        },
      });
    } else {
      getZipCodes();
    }
  }, [state?.filters?.city]);

  useEffect(() => {
    // if (state.filters.selectedType === "cityPredictions") {
    //   setZipCodesPredictions([]);
    //   getCityPredictionCount();
    // } else if (state.filters.selectedType === "msaPredictions") {
    //   setZipCodesPredictions([]);
    //   getMsaCount();
    // } else if (state.filters.selectedType === "zipcodePredictions") {
    //   setZipCodesPredictions([]);
    //   getZipcodePredictionCount();
    // }
  }, [state.filters.selectedType, state?.filters?.state]);

  const getMsaCount = async () => {
    const { cityPredValues, predictionBoundary } =
      await setPredictionBoundary();
    const response = await axios.get(
      `${baseURL}getMetroPredictionSummary?state=${state?.filters?.state}&date=${cityPredValues[0]}`
    );
    const count = response.data?.cube?.length;
    setPredictionCount(count);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        noOfPrediction: count,
        predictionFor: cityPredValues[0],
        predictionTill: predictionBoundary,
      },
    });
  };

  const getCityPredictionCount = async () => {
    const { cityPredValues, predictionBoundary } =
      await setPredictionBoundary();
    const response = await axios.get(
      `${baseURL}getCityPredictions?state=${state?.filters?.state}&date=${cityPredValues[0]}`
    );
    const count = response.data?.cube?.length;
    setPredictionCount(count);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        noOfPrediction: count,
        predictionFor: cityPredValues[0],
        predictionTill: predictionBoundary,
      },
    });
  };

  const getZipcodePredictionCount = async () => {
    const { zipcodePredValues, predictionBoundary } =
      await setPredictionBoundary();
    const response = await axios.get(
      `${baseURL}getZipCodePredictionsSummary?state=${state?.filters?.state}&date=${zipcodePredValues[0]}`
    );
    const count = response.data?.cube?.length;
    setPredictionCount(count);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        noOfPrediction: count,
        zipcodeQuarterPredictionFor: zipcodePredValues[0],
        predictionTill: predictionBoundary,
      },
    });
  };

  const getStates = async () => {
    try {
      const response = await axios.get(
        `${baseURL}getState?email=${state.user.email}`
      );
      if (response.data?.length) {
        let temp = response.data?.map((item: any) => {
          return {
            name: item.state,
            coOrd: {
              lat: item.latitude,
              lng: item.longitude,
            },
            abbr: item.abbr,
          };
        });
        setStates(
          temp.sort((a: any, b: any) => {
            if (a.name < b.name) {
              return -1;
            }
            if (a.name > b.name) {
              return 1;
            }
            return 0;
          })
        );
        let filters = state.filters;
        filters.state = temp[0].name;
        dispatch({
          type: "APPLY_FILTER",
          payload: {
            ...filters,
            states: temp,
            stateCord: temp[0].coOrd,
            selectedTerritory: temp[0].name,
            state: temp[0].name,
          },
        });
      } else {
        setStates([]);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const getZipCodes = () => {
    console.log(states);
    let temp: any = [];
    if (state?.filters?.city?.length === 1) {
      state?.filters?.city?.forEach((cityName: any) => {
        cities.forEach((item: any) => {
          if (item.name === cityName) {
            if (item?.zipcodes.length) {
              temp = [...temp, ...item?.zipcodes?.split(",")];
            }
          }
        });
      });
    } else if (state?.filters?.city?.length > 1) {
      state?.filters?.city?.forEach((cityName: any) => {
        cities.forEach((item: any) => {
          if (item.name === cityName) {
            const temp2 = item?.zipcodes
              .split(",")
              .map((code: any) => `${code}_${cityName}`);
            temp = [...temp, ...temp2];
          }
        });
      });
    }
    setZipCodes(temp);
  };

  const setPredictionBoundary = async () => {
    const selectedState = state?.filters?.state;
    const predictionTill = state?.filters?.predictionTill;
    let cityPredValues = [];
    let zipcodePredValues = [];
    let predictionBoundary: any = {};
    if (selectedState) {
      const predictionTillData = predictionTill[selectedState.toLowerCase()];
      if (
        predictionTillData &&
        predictionTillData.cityPredValues &&
        predictionTillData.zipcodePredValues
      ) {
        predictionBoundary = predictionTill;
        cityPredValues = predictionTillData.cityPredValues;
        zipcodePredValues = predictionTillData.zipcodePredValues;
      } else {
        predictionBoundary = await generateDropDownRanges();
        // @ts-ignore
        const predictionBoundaryByState =
          predictionBoundary[selectedState.toLowerCase()];
        cityPredValues = predictionBoundaryByState.cityPredValues;
        zipcodePredValues = predictionBoundaryByState.zipcodePredValues;
      }
    }

    setDates(cityPredValues);
    setQYears(zipcodePredValues);
    return { cityPredValues, zipcodePredValues, predictionBoundary };
  };

  const handleStateChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event;
    // @ts-ignore
    let Hcenter = {
      lat: "",
      lng: "",
    };
    const temp = states.find((t: any) => t.name === value);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        state: event.target.value as string,
        stateCord: states.filter(
          (s: { name: string }) => s.name === (event.target.value as string)
        )[0].coOrd,
        selectedTerritory: temp?.name,
        heatMapCenter: Hcenter,
        showFloodMap: false,
        showSchoolMap: false,
        showFireMap: false,
        showHurricaneMap: false,
        showHeatMap: true,
      },
    });
  };
  const handleUsStateChange = (event: SelectChangeEvent<[]>) => {
    const {
      target: { value },
    } = event;
    // @ts-ignore
    let Hcenter = {
      lat: "",
      lng: "",
    };
    const temp = states.find((t: any) => t.name === value);
    // if(temp?.name==='Florida'){
    //   setShowFloodMap(true)
    //   setShowMarkers(false)
    // }

    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        state: event.target.value as string,
        stateCord: states.filter(
          (s: { name: string }) => s.name === (event.target.value as string)
        )[0].coOrd,
        selectedTerritory: temp?.name,
        heatMapCenter: Hcenter,
        showFloodMap: false,
        showSchoolMap: false,
        showFireMap: false,
        showHurricaneMap: false,
        showHeatMap: true,
      },
    });
  };

  const handleHeapMapMonthChange = (event: SelectChangeEvent) => {
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        heatMapMonths: event.target.value,
        showHeatMap: true,
      },
    });
  };

  const handleHeapMapMilesChange = (event: SelectChangeEvent) => {
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        heatMapMiles: event.target.value,
        showHeatMap: true,
      },
    });
  };

   const getCitiesByState = async () => {
      const { zipcodePredValues } = await setPredictionBoundary();
      try {
        let cityInfo = {};
        let countyInfo: any = {};
        let citySelection = [];
        let msaInfo = {};
        let isEmptyCity = true;
        let firstZipcode = "";
        let firstMsa = "";
        let formattedData: any = [];
  
        const promises = [
          await getCityByState(state?.filters?.state),
          await getMetroByState(state?.filters?.state),
          await getCountyByState(state?.filters?.state),
        ];
        const responses = await Promise.all(promises);
        const cityResponse: any = responses[0];
        const msaResponse = responses[1];
        const countyResponse: any = responses[2];
  
        if (cityResponse?.data && cityResponse?.selection) {
          isEmptyCity = false;
          cityInfo = cityResponse?.data;
          citySelection = cityResponse?.selection;
          setCities(cityInfo);
          firstZipcode = citySelection[0][0];
        }
        if (countyResponse?.data) {
          isEmptyCity = false;
          countyInfo = countyResponse?.data;
          setCounty(countyInfo);
        }
  
        if (msaResponse?.data) {
          isEmptyCity = false;
          msaInfo = msaResponse?.data;
          setMsa(msaInfo);
          // @ts-ignore
          firstMsa = msaInfo[0];
        }
  
        if (isEmptyCity) {
          setCities([]);
          dispatch({
            type: "APPLY_FILTER",
            payload: {
              ...state.filters,
              city: [],
            },
          });
          citySelection = [];
        } else {
          citySelection = flatten(citySelection);
          formattedData = citySelection.map((item: any) => {
            const [number, location] = item.split("_");
            const paddedNumber = number.padStart(5, "0");
            return `${paddedNumber}_${location}`;
          });
          // @ts-ignore
          const cityName = cityInfo[0]?.name;
          const countyName = countyInfo[0]?.name;
          dispatch({
            type: "APPLY_FILTER",
            payload: {
              ...state.filters,
              city: state.filters.cityRedirected
                ? state.filters.city
                : [cityName],
              county: [countyName],
              citiesInfo: cityInfo,
              countyInfo: countyInfo,
              zipcodePrediction: [formattedData[0]],
              quarterPredictionFor: zipcodePredValues[0],
              msaInfo: msaInfo,
              msa: [firstMsa],
            },
          });
        }
        citySelection = flatten(citySelection);
        formattedData = citySelection.map((item: any) => {
          const [number, location] = item.split("_");
          const paddedNumber = number.padStart(5, "0");
          return `${paddedNumber}_${location}`;
        });
        setZipCodes(citySelection);
        setZipCodesPredictions(formattedData.length === 1 ? [] : formattedData);
      } catch (err) {
        console.log("error", err);
      }
    };

  const flatten = (arr: any) => {
    return arr.reduce((flat: string | any[], toFlatten: any) => {
      return flat.concat(
        Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten
      );
    }, []);
  };

  const handleValueChange = (event: SelectChangeEvent) => {
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        value: event.target.value as string,
        showInterval: false,
      },
    });
  };

  const handleDemographicValueChange = (event: SelectChangeEvent) => {
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        demographicValue: event.target.value as string,
        trackEvent: "demographicValueChange",
      },
    });
  };
  const handleRentalValueChange = (event: SelectChangeEvent) => {
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        rentalValue: event.target.value as string,
        trackEvent: "rentalValueChange",
      },
    });
  };

  const handleCrimeTypeChange = (event: SelectChangeEvent) => {
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        crimeType: event.target.value as string,
      },
    });
  };

  const handleProfitabilityChange = (event: SelectChangeEvent<[]>) => {
    const {
      target: { value },
    } = event;
    setProfitability(value);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        profitability: value,
        showHeatMap: true,
      },
    });
  };

  const handleSchoolTypeChange = (event: SelectChangeEvent<[]>) => {
    const {
      target: { value },
    } = event;
    setSchoolType(value);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        schoolType: value,
      },
    });
  };
  const handleSchoolRatingChange = (event: SelectChangeEvent<[]>) => {
    const {
      target: { value },
    } = event;
    setSchoolRating(value);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        schoolRating: value,
      },
    });
  };
  
  const handleCountyDelete = (val: any) => {
    const temp = [...state?.filters?.county];
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        county: temp.filter((name) => name !== val),
        zipcode: [],
        trackEvent: "cityChange",
      },
    });
  };

  const handleCityChangeAutocomplete = (value: any) => {
    let selectedValues = [...state.filters.city, ...[value[value.length - 1]]];
    // @ts-ignore
    selectedValues = [...new Set(selectedValues)].filter((item) => item);
    console.log(selectedValues);
    console.log(state);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        city: selectedValues,
        trackEvent: "cityChange",
        zipcode: [],
        neighborhood: [],
        showInterval: cities?.length === 0 && state.filters.showInterval,
        adjustedPredictions:
          cities?.length === 0 && state.filters.adjustedPredictions,
        submitAdjustedPrediction:
          cities?.length === 0 && state.filters.submitAdjustedPrediction,
        refreshAdjustedPredictionSubmit:
          cities?.length === 0 && state.filters.refreshAdjustedPredictionSubmit,
        adjustedSentimentsByConsumer:
          cities?.length === 0 && state.filters.adjustedSentimentsByConsumer,
        submitAdjustedSentimentByConsumer:
          cities?.length === 0 &&
          state.filters.submitAdjustedSentimentByConsumer,
        refreshAdjustedSentimentSubmitByConsumer:
          cities?.length === 0 &&
          state.filters.refreshAdjustedSentimentSubmitByConsumer,
        adjustedSentimentsByBuilder:
          cities?.length === 0 && state.filters.adjustedSentimentsByBuilder,
        submitAdjustedSentimentByBuilder:
          cities?.length === 0 &&
          state.filters.submitAdjustedSentimentByBuilder,
        refreshAdjustedSentimentSubmitByBuilder:
          cities?.length === 0 &&
          state.filters.refreshAdjustedSentimentSubmitByBuilder,
        predictionsArray:
          selectedValues.length > 1 ? [] : state.filters.predictionsArray,
      },
    });
  };
  const handleCountyChangeAutocomplete = (value: any) => {
    let selectedValues = [
      ...state.filters.county,
      ...[value[value.length - 1]],
    ];
    // @ts-ignore
    selectedValues = [...new Set(selectedValues)].filter((item) => item);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        county: selectedValues,
      },
    });
  };

  const handleSingleCityChangeAutocomplete = (value: any) => {
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        city: [value],
      },
    });
  };

  const handleSingleZipcodeChangeAutocomplete = (value: any) => {
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        zipcodePrediction: [value],
      },
    });
  };

  const handleSingleCountyChangeAutocomplete = (value: any) => {
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        county: [value],
      },
    });
  };

  const handleSingleMSAChangeAutocomplete = (value: any) => {
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        msa: [value],
      },
    });
  };

  const handleMSAChangeAutocomplete = (value: any) => {
    let selectedValues = [...state.filters.msa, ...[value[value.length - 1]]];
    // @ts-ignore
    selectedValues = [...new Set(selectedValues)].filter((item) => item);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        zipcode: [],
        neighborhood: [],
        msa: selectedValues,
        showInterval: msa?.length === 0 && state.filters.showInterval,
        adjustedPredictions:
          msa?.length === 0 && state.filters.adjustedPredictions,
        submitAdjustedPrediction:
          msa?.length === 0 && state.filters.submitAdjustedPrediction,
        refreshAdjustedPredictionSubmit:
          msa?.length === 0 && state.filters.refreshAdjustedPredictionSubmit,
        adjustedSentimentsByConsumer:
          msa?.length === 0 && state.filters.adjustedSentimentsByConsumer,
        submitAdjustedSentimentByConsumer:
          msa?.length === 0 && state.filters.submitAdjustedSentimentByConsumer,
        refreshAdjustedSentimentSubmitByConsumer:
          msa?.length === 0 &&
          state.filters.refreshAdjustedSentimentSubmitByConsumer,
        adjustedSentimentsByBuilder:
          msa?.length === 0 && state.filters.adjustedSentimentsByBuilder,
        submitAdjustedSentimentByBuilder:
          msa?.length === 0 && state.filters.submitAdjustedSentimentByBuilder,
        refreshAdjustedSentimentSubmitByBuilder:
          msa?.length === 0 &&
          state.filters.refreshAdjustedSentimentSubmitByBuilder,
        predictionsArray:
          selectedValues.length > 1 ? [] : state.filters.predictionsArray,
      },
    });
  };

  const handlePredictionChange = (event: SelectChangeEvent) => {
    console.log(event.target);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        predictionFor: event.target.value as string,
      },
    });
  };

  const handleQuarterPredictionChange = (event: SelectChangeEvent) => {
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        quarterPredictionFor: event.target.value as string,
      },
    });
  };

  const handleZipcodeQuarterPredictionChange = (event: SelectChangeEvent) => {
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        zipcodeQuarterPredictionFor: event.target.value as string,
        trackEvent: "zipCodeChange",
      },
    });
  };

  const handleZipCodeChange = (event: SelectChangeEvent<[]>) => {
    const {
      target: { value },
    } = event;
    const zipcodes = typeof value === "string" ? value.split(",") : value;
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        zipcode: typeof value === "string" ? value.split(",") : value,
        showInterval:
          zipcodes?.length > 1 && state.filters.showInterval
            ? false
            : state.filters.showInterval,
        trackEvent: "zipCodeChange",
      },
    });
  };

  const handleZipCodePredictionChange = (value: any) => {
    const selectedValues = [
      ...state.filters.zipcodePrediction,
      ...[value[value.length - 1]],
    ];
    const zipcodeInfo = zipCodesPredictions.filter(
      (z: any) => !value.includes(z)
    );
    setZipCodesPredictions(zipcodeInfo);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        zipcodePrediction: selectedValues,
        showInterval:
          selectedValues.length === 0 && state?.filters.showInterval,
        adjustedPredictions:
          selectedValues?.length === 0 && state.filters.adjustedPredictions,
        submitAdjustedPrediction:
          selectedValues?.length === 0 &&
          state.filters.submitAdjustedPrediction,
        refreshAdjustedPredictionSubmit:
          selectedValues?.length === 0 &&
          state.filters.refreshAdjustedPredictionSubmit,
        adjustedSentimentsByConsumer:
          selectedValues?.length === 0 &&
          state.filters.adjustedSentimentsByConsumer,
        submitAdjustedSentimentByConsumer:
          selectedValues?.length === 0 &&
          state.filters.submitAdjustedSentimentByConsumer,
        refreshAdjustedSentimentSubmitByConsumer:
          selectedValues?.length === 0 &&
          state.filters.refreshAdjustedSentimentSubmitByConsumer,
        adjustedSentimentsByBuilder:
          selectedValues?.length === 0 &&
          state.filters.adjustedSentimentsByBuilder,
        submitAdjustedSentimentByBuilder:
          selectedValues?.length === 0 &&
          state.filters.submitAdjustedSentimentByBuilder,
        refreshAdjustedSentimentSubmitByBuilder:
          selectedValues?.length === 0 &&
          state.filters.refreshAdjustedSentimentSubmitByBuilder,
        predictionsArray:
          selectedValues.length > 1 ? [] : state.filters.predictionsArray,
        trackEvent: "zipCodeChange",
      },
    });
  };

  const handleTopPredictionChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    setPrediction(newValue as number);
  };

  useEffect(() => {
    if (JSON.stringify(state.filters.years) !== JSON.stringify(year)) {
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          years: year,
        },
      });
    }
  }, [debounceYear]);

  useEffect(() => {
    if (state.filters.noOfPrediction !== prediction) {
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          noOfPrediction: prediction,
        },
      });
    }
  }, [debouncePredictions]);

  const handleYearChange = (event: Event, newValue: number | number[]) => {
    setYear(newValue as number[]);
  };

  function valuetext(value: number) {
    return `${value}`;
  }

  function valueLabelFormat(value: number) {
    return value;
  }

  const handleCityDelete = (val: any) => {
    const temp = [...state?.filters?.city];
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        city: temp.filter((name) => name !== val),
        zipcode: [],
        trackEvent: "cityChange",
      },
    });
  };
  const handleHomeSalesDelete = (value: any) => {
    const temp = [...state?.filters?.homeSalesArray];
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        homeSalesArray: temp.filter((name) => name !== value),
        reSales:value==='resale'?!state.filters.reSales:state.filters.reSales,
        newSales:value==='new_sale'?!state.filters.newSales:state.filters.newSales,
        totalSales:value==='total_sales'?!state.filters.totalSales:state.filters.totalSales,
      },
    });
  };

  const handleHomeSalesChange = (value: any, bool?: boolean) => {
    if (bool) {
      handleHomeSalesDelete(value);
    } else {
      let selectedValues = [...state.filters.homeSalesArray, value];
      // @ts-ignore
      selectedValues = [...new Set(selectedValues)].filter((item) => item);
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          homeSalesArray: selectedValues,
          reSales:value==='resale'?!state.filters.reSales:state.filters.reSales,
          newSales:value==='new_sale'?!state.filters.newSales:state.filters.newSales,
          totalSales:value==='total_sales'?!state.filters.totalSales:state.filters.totalSales,
        },
      });
    }
  };


  const handlePredictionDelete = (val: any) => {
    console.log(
      [...state?.filters?.predictionsArray],
      val,
      val === "Mortgage Rate"
    );
    const temp = [...state?.filters?.predictionsArray];
    console.log(
      temp,
      temp.filter((name) => name !== val)
    );
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        predictionsArray: temp.filter((name) => name !== val),
        zipcode: [],
        adjustedPredictions:
          val === "Mortgage Rates"
            ? !state.filters.adjustedPredictions
            : state.filters.adjustedPredictions,
        adjustedSentimentsByConsumer:
          val === "Consumer Sentiments(HPSI)"
            ? !state.filters.adjustedSentimentsByConsumer
            : state.filters.adjustedSentimentsByConsumer,
        adjustedSentimentsByBuilder:
          val === "Builder Sentiments(NAHB)"
            ? !state.filters.adjustedSentimentsByBuilder
            : state.filters.adjustedSentimentsByBuilder,
        refreshAdjustedPredictionSubmit:
          val === "Mortgage Rates"
            ? !state.filters.refreshAdjustedPredictionSubmit
            : state.filters.refreshAdjustedPredictionSubmit,
        refreshAdjustedSentimentSubmitByConsumer:
          val === "Consumer Sentiments(HPSI)"
            ? !state.filters.refreshAdjustedSentimentSubmitByConsumer
            : state.filters.refreshAdjustedSentimentSubmitByConsumer,
        refreshAdjustedSentimentSubmitByBuilder:
          val === "Builder Sentiments(NAHB)"
            ? !state.filters.refreshAdjustedSentimentSubmitByBuilder
            : state.filters.refreshAdjustedSentimentSubmitByBuilder,
      },
    });
    setSelectedPredictions(temp.filter((name) => name !== val));
  };

  const handleMSADelete = (val: any) => {
    const temp = [...state?.filters?.msa];
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        msa: temp.filter((t) => t.name !== val.name),
        zipcode: [],
      },
    });
  };

  const handleMarker = (event: any) => {
    setShowMarkers(event.target.checked);
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        showMarkers: event.target.checked,
        showHeatMap: true,
      },
    });
  };

  const handleZipcodeDelete = (val: any) => {
    const temp = [...state?.filters?.zipcode];
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        zipcode: temp.filter((name) => name !== val),
        trackEvent: "zipCodeChange",
      },
    });
  };

  const sort_zipcodes = (arr: any) => {
    arr.sort((a: any, b: any) => {
      const alphaA = a.split("_")[1];
      const alphaB = b.split("_")[1];

      if (alphaA < alphaB) {
        return -1;
      } else if (alphaA > alphaB) {
        return 1;
      } else {
        return 0;
      }
    });
    return arr;
  };

  const handleZipcodePredictionDelete = (val: any) => {
    const temp = [...state?.filters?.zipcodePrediction];
    const zipcodeInfo = [...zipCodesPredictions];
    zipcodeInfo.push(val);
    setZipCodesPredictions(sort_zipcodes(zipcodeInfo));
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        zipcodePrediction: temp.filter((name) => name !== val),
        trackEvent: "zipCodeChange",
      },
    });
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value;
    // zipcodePrediction
    if (state?.filters?.city?.length) {
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          selectedType: value,
          regionType: "Zipcode",
          showFireMap: false,
          showFloodMap: false,
          showSchoolMap: false,
          showHeatMap: true,
          showHurricaneMap: false,
          trackEvent: "selectedtypeChange",
        },
      });
    } else {
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          selectedType: value,
          regionType: "Zipcode",
          city: cities?.length ? ["Lehigh Acres"] : [],
          showFireMap: false,
          showFloodMap: false,
          showSchoolMap: false,
          showHeatMap: true,
          showHurricaneMap: false,
          trackEvent: "selectedtypeChange",
        },
      });
    }
  };

  const handleIntervalChange = () => {
    let predictionValue = { ...state.filters.defaultAdjustedPredictionValues };
    let predictionValueConsumer = {
      ...state.filters.defaultAdjustedSentimentValuesByConsumer,
    };
    let predictionValueBuilder = {
      ...state.filters.defaultAdjustedSentimentValuesByBuilder,
    };

    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        showInterval: !state?.filters?.showInterval,
        adjustedPredictions: false,
        adjustedPredictionValues: predictionValue,
        submitAdjustedPrediction: false,
        refreshAdjustedPredictionSubmit: false,
        adjustedSentimentsByConsumer: false,
        adjustedSentimentValuesByConsumer: predictionValueConsumer,
        submitAdjustedSentimentByConsumer: false,
        refreshAdjustedSentimentSubmitByConsumer: false,
        adjustedSentimentsByBuilder: false,
        adjustedSentimentValuesByBuilder: predictionValueBuilder,
        submitAdjustedSentimentByBuilder: false,
        refreshAdjustedSentimentSubmitByBuilder: false,
        predictionsArray: [],
        trackEvent: "intervalChange",
      },
    });
  };

  const getAdjustedText = () => {
    switch (adjustedRateFor) {
      case "Mortgage Rates":
        return showConfirmation
          ? "Changed Adjusted Predictions To Mortgage Rates"
          : "Adjusted Predictions To Mortgage Rates";
      case "Consumer Sentiments(HPSI)":
        return showConfirmation
          ? "Changed Adjusted Predictions To Consumer Sentiments(HPSI) Rates"
          : "Adjusted Predictions To Consumer Sentiments(HPSI) Rates";
      case "Builder Sentiments(NAHB)":
        return showConfirmation
          ? "Changed Adjusted Predictions To Builder Sentiments(NAHB) Rates"
          : "Adjusted Predictions To Builder Sentiments(NAHB) Rates";
      default:
        return null;
    }
  };

  const handlePredictionChangeAutocomplete = (value: any, bool?: boolean) => {
    console.log(value, bool);
    if (bool) {
      handlePredictionDelete(value);
    } else {
      let selectedValues = [...state.filters.predictionsArray, value];
      // @ts-ignore
      selectedValues = [...new Set(selectedValues)].filter((item) => item);
      setSelectedPredictions(selectedValues);
      console.log(selectedValues);
      console.log(state);
      // dispatch({
      //   type: "APPLY_FILTER",
      //   payload: {
      //     ...state.filters,
      //     predictionsArray: selectedValues,
      //     zipcode: [],
      //     neighborhood: [],
      //     showInterval: cities?.length === 0 && state.filters.showInterval,
      //     adjustedPredictions:
      //       cities?.length === 0 && state.filters.adjustedPredictions,
      //     submitAdjustedPrediction:
      //       cities?.length === 0 && state.filters.submitAdjustedPrediction,
      //     refreshAdjustedPredictionSubmit:
      //       cities?.length === 0 && state.filters.refreshAdjustedPredictionSubmit,
      //   },
      // });
      if (value === "Mortgage Rates") {
        handleAdjustedPredictionChange(value);
      } else {
        handleAdjustedSentimentChange(value);
      }
    }
  };

  const handleAdjustedPredictionChange = (val: string) => {
    setPrevString(adjustedRateFor);
    setAdjustedRateFor(val);
    let refresh = state.filters.refreshAdjustedPredictionSubmit;
    let predictionValue = { ...state.filters.adjustedPredictionValues };
    if (!state?.filters?.adjustedPredictions) {
      handleOpenAdjustedPredictionPopup(true);
    } else {
      predictionValue.status = false;
      refresh = false;
    }

    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        showInterval: false,
        adjustedPredictions: !state?.filters?.adjustedPredictions,
        adjustedPredictionValues: predictionValue,
        submitAdjustedPrediction: false,
        refreshAdjustedPredictionSubmit: refresh,
        trackEvent: "adjustedPredictionChange",
        // adjustedSentimentsByConsumer: false,
        // adjustedSentimentsByBuilder: false,
        // submitAdjustedSentimentByConsumer: false,
        // refreshAdjustedSentimentSubmitByConsumer: false,
        // submitAdjustedSentimentByBuilder: false,
        // refreshAdjustedSentimentSubmitByBuilder: false,
      },
    });
  };

  const getSentimentState = (val: string) => {
    return val === "Consumer Sentiments(HPSI)"
      ? !state?.filters?.adjustedSentimentsByConsumer
      : !state?.filters?.adjustedSentimentsByBuilder;
  };

  const handleAdjustedSentimentChange = (val: string) => {
    setPrevString(adjustedRateFor);
    setAdjustedRateFor(val);
    let refresh =
      adjustedRateFor == "Consumer Sentiments(HPSI)"
        ? state.filters.refreshAdjustedSentimentSubmitByConsumer
        : state.filters.refreshAdjustedSentimentSubmitByBuilder;
    let predictionValue =
      val == "Consumer Sentiments(HPSI)"
        ? { ...state.filters.adjustedSentimentValuesByConsumer }
        : { ...state.filters.adjustedSentimentValuesByBuilder };
    console.log("jkl", state.filters, refresh);
    if (getSentimentState(val)) {
      handleOpenAdjustedSentimentPopup(true);
    } else {
      predictionValue.status = false;
      refresh = false;
    }
    if (val === "Consumer Sentiments(HPSI)") {
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          showInterval: false,
          adjustedSentimentsByConsumer:
            !state?.filters?.adjustedSentimentsByConsumer,
          adjustedSentimentValuesByConsumer: predictionValue,
          submitAdjustedSentimentByConsumer: false,
          refreshAdjustedSentimentSubmitByConsumer: refresh,
          trackEvent: "adjustedPredictionChange",
          // adjustedPredictions: false,
          // submitAdjustedPrediction: false,
          // refreshAdjustedPredictionSubmit: false,
          // submitAdjustedSentimentByBuilder: false,
          // refreshAdjustedSentimentSubmitByBuilder: false,
          // adjustedSentimentsByBuilder: false,
        },
      });
    } else {
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          showInterval: false,
          adjustedSentimentsByBuilder:
            !state?.filters?.adjustedSentimentsByBuilder,
          adjustedSentimentValuesByBuilder: predictionValue,
          submitAdjustedSentimentByBuilder: false,
          refreshAdjustedSentimentSubmitByBuilder: refresh,
          trackEvent: "adjustedPredictionChange",
          // adjustedPredictions: false,
          // submitAdjustedPrediction: false,
          // refreshAdjustedPredictionSubmit: false,
          // submitAdjustedSentimentByConsumer: false,
          // refreshAdjustedSentimentSubmitByConsumer: false,
          // adjustedSentimentsByConsumer: false,
        },
      });
    }
  };

  const handleOpenAdjustedPredictionPopup = (isOpen: boolean) => {
    setShowConfirmation(false);
    setShowAdjustedPredictionPopUp(isOpen);
    if (!isOpen) {
      if (state?.filters?.adjustedPredictions) {
        dispatch({
          type: "APPLY_FILTER",
          payload: {
            ...state.filters,
            adjustedPredictions: false,
            submitAdjustedPrediction: false,
          },
        });
      }
    }
    if (state?.filters?.refreshAdjustedPredictionSubmit) {
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          refreshAdjustedPredictionSubmit: false,
        },
      });
    }
  };

  const handleOpenAdjustedSentimentPopup = (isOpen: boolean) => {
    setShowConfirmation(false);
    setShowAdjustedPredictionPopUp(isOpen);
    if (!isOpen) {
      if (state?.filters?.adjustedSentimentsByConsumer) {
        dispatch({
          type: "APPLY_FILTER",
          payload: {
            ...state.filters,
            adjustedSentimentsByConsumer: false,
            submitAdjustedSentimentByConsumer: false,
          },
        });
      }
      if (state?.filters?.adjustedSentimentsByBuilder) {
        dispatch({
          type: "APPLY_FILTER",
          payload: {
            ...state.filters,
            adjustedSentimentsByBuilder: false,
            submitAdjustedSentimentByBuilder: false,
          },
        });
      }
    }
    if (state?.filters?.refreshAdjustedSentimentSubmitByConsumer) {
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          refreshAdjustedSentimentSubmitByConsumer: false,
        },
      });
    }
    if (state?.filters?.refreshAdjustedSentimentSubmitByBuilder) {
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          refreshAdjustedSentimentSubmitByBuilder: false,
        },
      });
    }
  };

  // const sliderChange = (event: any, newValue: number | number[]) => {
  //   let predictionValue = {...state.filters.adjustedPredictionValues}
  //   predictionValue.status = true
  //   predictionValue[event.target.name] = newValue
  //   dispatch({
  //     type: 'APPLY_FILTER',
  //     payload: {
  //       ...state.filters,
  //       showInterval: false,
  //       adjustedPredictionValues: predictionValue
  //     },
  //   });
  // };
  const sliderChange = (event: any, newValue: number | number[]) => {
    console.log(event.target.name, newValue, adjustedRateFor, state.filters);
    if (adjustedRateFor == "Mortgage Rates") {
      let predictionValue = { ...state.filters.adjustedPredictionValues };
      predictionValue.status = true;
      predictionValue[event.target.name] = newValue;
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          showInterval: false,
          adjustedPredictionValues: predictionValue,
        },
      });
    } else if (adjustedRateFor == "Consumer Sentiments(HPSI)") {
      let predictionValue = {
        ...state.filters.adjustedSentimentValuesByConsumer,
      };
      console.log(predictionValue);
      predictionValue.status = true;
      predictionValue[event.target.name] = newValue;
      console.log(predictionValue);
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          showInterval: false,
          adjustedSentimentValuesByConsumer: predictionValue,
        },
      });
    } else {
      let predictionValue = {
        ...state.filters.adjustedSentimentValuesByBuilder,
      };
      predictionValue.status = true;
      predictionValue[event.target.name] = newValue;
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          showInterval: false,
          adjustedSentimentValuesByBuilder: predictionValue,
        },
      });
    }
  };

  const handleConfirmation = () => {
    console.log(state.filters, adjustedRateFor);
    const mortByQuarters =
      adjustedRateFor == "Mortgage Rates"
        ? { ...state.filters.adjustedPredictionValues }
        : adjustedRateFor == "Consumer Sentiments(HPSI)"
        ? { ...state.filters.adjustedSentimentValuesByConsumer }
        : { ...state.filters.adjustedSentimentValuesByBuilder };
    console.log(mortByQuarters);
    delete mortByQuarters.status;
    setConfirmationValues(mortgageRateCalculation(mortByQuarters));
    setShowConfirmation(true);
  };

  // const handleSubmit = () => {
  //   handleOpenAdjustedPredictionPopup(false)
  //   dispatch({
  //     type: 'APPLY_FILTER',
  //     payload: {
  //       ...state.filters,
  //       submitAdjustedPrediction: true,
  //       refreshAdjustedPredictionSubmit: true
  //     },
  //   });
  // }

  const handleSubmit = () => {
    handleOpenAdjustedPredictionPopup(false);
    console.log(adjustedRateFor, selectedPredictions);
    //  dispatch({
    //     type: "APPLY_FILTER",
    //     payload: {
    //       ...state.filters,
    //       predictionsArray: render,
    //     },
    //   });

    if (adjustedRateFor == "Mortgage Rates") {
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          predictionsArray: selectedPredictions,
          submitAdjustedPrediction: true,
          refreshAdjustedPredictionSubmit: true,
          // adjustedSentimentsByBuilder: false,
          // adjustedSentimentsByConsumer: false,
          refreshAdjustedSentimentSubmitByBuilder: false,
          refreshAdjustedSentimentSubmitByConsumer: false,
        },
      });
    } else if (adjustedRateFor == "Consumer Sentiments(HPSI)") {
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          submitAdjustedSentimentByConsumer: true,
          refreshAdjustedSentimentSubmitByConsumer: true,
          predictionsArray: selectedPredictions,
          refreshAdjustedSentimentSubmitByBuilder: false,
          // adjustedSentimentsByBuilder: false,
          // adjustedPredictions: false,
          refreshAdjustedPredictionSubmit: false,
        },
      });
    } else {
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          submitAdjustedSentimentByBuilder: true,
          refreshAdjustedSentimentSubmitByBuilder: true,
          predictionsArray: selectedPredictions,
          refreshAdjustedSentimentSubmitByConsumer: false,
          // adjustedPredictions: false,
          // adjustedSentimentsByConsumer: false,
          refreshAdjustedPredictionSubmit: false,
        },
      });
    }
  };
  console.log(state?.filters?.predictionFor, dates[0]);

  return (
    <Drawer variant="permanent" className="container" open={state?.drawerOpen}>
      <div className="filterContainer">
        {state?.filters?.selectedType !== "heatMap" && (
          <FormControl sx={{ m: 1, width: 300 }} variant="standard">
            <InputLabel shrink id="demo-simple-select-label">
              Select State
            </InputLabel>
            <Select
              value={state?.filters?.state}
              label="Select State"
              onChange={handleStateChange}
              size="small"
            >
              {states?.map((state: any, index: number) => (
                <MenuItem value={state.name} key={index}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {state?.filters?.selectedType == "heatMap" && (
          <FormControl sx={{ m: 1, width: 300 }} variant="standard">
            <InputLabel shrink id="demo-simple-select-label">
              Select State
            </InputLabel>
            <Select
              value={state?.filters?.selectedTerritory}
              label="Select State"
              onChange={handleUsStateChange}
              size="small"
            >
              {states?.map((state: any, index: number) => (
                <MenuItem value={state.name} key={index}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl sx={{ m: 1, width: 300 }}>
          <RadioGroup
            value={state?.filters?.selectedType}
            onChange={!state.filters.stateLoading && handleTypeChange}
          >
            <FormControlLabel
              value={"predictedMetrics"}
              control={<Radio style={{ color: "#fff" }} />}
              label="City Predictions"
            />
            <FormControlLabel
              value={"zipcodePredictedMetrics"}
              control={<Radio style={{ color: "#fff" }} />}
              label="Zipcode Predictions"
            />
            <FormControlLabel
              value={"msaPredictedMetrics"}
              control={<Radio style={{ color: "#fff" }} />}
              label="MSA Predictions"
            />
            <FormControlLabel
              value={"countyPredictedMetrics"}
              control={<Radio style={{ color: "#fff" }} />}
              label="County Predictions"
            />
            <FormControlLabel
              value={"cityClusters"}
              control={<Radio style={{ color: "#fff" }} />}
              label="City Clusters"
            />
            <FormControlLabel
              value={"cityPredictions"}
              control={<Radio style={{ color: "#fff" }} />}
              label="City Prediction Summary"
            />
            <FormControlLabel
              value={"zipcodePredictions"}
              control={<Radio style={{ color: "#fff" }} />}
              label="Zipcode Prediction Summary"
            />
            <FormControlLabel
              value={"msaPredictions"}
              control={<Radio style={{ color: "#fff" }} />}
              label="MSA Prediction Summary"
            />
               <FormControlLabel
              value={"countyPredictions"}
              control={<Radio style={{ color: "#fff" }} />}
              label="County Prediction Summary"
            />
            <FormControlLabel
              value={"economicIndicators"}
              control={<Radio style={{ color: "#fff" }} />}
              label="Economic Indicators"
            />
            <FormControlLabel
              value={"heatMap"}
              control={<Radio style={{ color: "#fff" }} />}
              label={
                state?.filters?.selectedType === "heatMap" ? (
                  <>
                    HeatMap &nbsp;{" "}
                    <InfoOutlinedIcon
                      className="limit-text-icon"
                      onClick={handleClickOpenDialog}
                    />
                    <BootstrapDialog
                      onClose={handleCloseDialog}
                      aria-labelledby="customized-dialog-title"
                      open={openDialog}
                    >
                      <BootstrapDialogTitle
                        id="customized-dialog-title"
                        onClose={handleCloseDialog}
                      >
                        Helpful Tips
                      </BootstrapDialogTitle>
                      <DialogContent dividers>
                        {returnHeatMapText()}
                      </DialogContent>
                    </BootstrapDialog>
                  </>
                ) : (
                  "HeatMap"
                )
              }
            />
            {/*<FormControlLabel*/}
            {/*  value={'whatIfAnalysis'}*/}
            {/*  control={<Radio style={{color: '#fff'}}/>}*/}
            {/*  label='What If Analysis City'*/}
            {/*/>*/}
            {/*<FormControlLabel*/}
            {/*  value={'whatIfAnalysis2'}*/}
            {/*  control={<Radio style={{color: '#fff'}}/>}*/}
            {/*  label='What If Analysis MSA'*/}
            {/*/>*/}
            <FormControlLabel
              value={"whatIfMulti"}
              control={<Radio style={{ color: "#fff" }} />}
              label={
                state?.filters?.selectedType === "whatIfMulti" ? (
                  <>
                    What If Multi-Criteria &nbsp;{" "}
                    <InfoOutlinedIcon
                      className="what-if-text-icon"
                      onClick={handleClickOpenDialog}
                    />
                    <BootstrapDialog
                      onClose={handleCloseDialog}
                      aria-labelledby="customized-dialog-title"
                      open={openDialog}
                    >
                      <BootstrapDialogTitle
                        id="customized-dialog-title"
                        onClose={handleCloseDialog}
                      >
                        Helpful Tips
                      </BootstrapDialogTitle>
                      <DialogContent dividers>
                        {returnWhatIfCriteriaText()}
                      </DialogContent>
                    </BootstrapDialog>
                  </>
                ) : (
                  "What If Multi-Criteria"
                )
              }
            />
            <FormControlLabel
              value={"whatIfMultiMSA"}
              control={<Radio style={{ color: "#fff" }} />}
              label={
                state?.filters?.selectedType === "whatIfMultiMSA" ? (
                  <>
                    What If Multi-Criteria MSA &nbsp;
                    <InfoOutlinedIcon
                      className="what-if-text-icon"
                      onClick={handleClickOpenDialog}
                    />
                    <BootstrapDialog
                      onClose={handleCloseDialog}
                      aria-labelledby="customized-dialog-title"
                      open={openDialog}
                    >
                      <BootstrapDialogTitle
                        id="customized-dialog-title"
                        onClose={handleCloseDialog}
                      >
                        Helpful Tips
                      </BootstrapDialogTitle>
                      <DialogContent dividers>
                        {returnWhatIfCriteriaText()}
                      </DialogContent>
                    </BootstrapDialog>
                  </>
                ) : (
                  "What If Multi-Criteria MSA"
                )
              }
            />
            {/* <FormControlLabel
              value={"warehouseSelection"}
              control={<Radio style={{ color: "#fff" }} />}
              label="Warehouse Selection"
            /> */}
          </RadioGroup>
        </FormControl>
        {state?.filters?.selectedType === "predictedMetrics" && (
          <>
            <FormControl sx={{ m: 1, width: 300 }} variant="standard">
              <InputLabel
                id="demo-multiple-checkbox-label"
                className="custom-auto-complete-label"
                shrink
              >
                Select City
              </InputLabel>
              {DISABLE_MULTI_SELECT.includes(state?.selectedTab) ? (
                <Autocomplete
                  className="custom-auto-select"
                  key="predictedMetrics-options"
                  id="prediction-metric-tags"
                  onChange={(e, v) => handleSingleCityChangeAutocomplete(v)}
                  options={cities.map((c: any) => c.name)}
                  defaultValue={
                    state?.filters?.city.length > 0
                      ? state?.filters?.city[0]
                      : state?.filters?.city
                  }
                  value={
                    state?.filters?.city.length > 0
                      ? state?.filters?.city[0]
                      : state?.filters?.city
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder="City" />
                  )}
                />
              ) : (
                <Autocomplete
                  multiple
                  className="custom-auto-select"
                  key="predictedMetrics-options"
                  id="prediction-metric-tags"
                  onChange={(e, v) => handleCityChangeAutocomplete(v)}
                  options={cities.map((c: any) => c.name)}
                  defaultValue={state?.filters?.city}
                  value={state?.filters?.city}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="City" />
                  )}
                  renderTags={() => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {state?.filters?.city.map((value: any) => (
                        <Chip
                          key={value}
                          label={value}
                          color="primary"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          onDelete={() => handleCityDelete(value)}
                        />
                      ))}
                    </Box>
                  )}
                />
              )}
            </FormControl>
            {DEMOGRAPHIC_DROPDOWN.includes(state?.selectedTab) &&
              state?.filters?.city?.length > 0 && (
                <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                  <InputLabel id="demo-simple-select-label" shrink>
                    Select value as
                  </InputLabel>
                  <Select
                    value={state?.filters?.demographicValue}
                    label="Select value as"
                    onChange={handleDemographicValueChange}
                  >
                    <MenuItem value={"Age"}>Age</MenuItem>
                    <MenuItem value={"Education"}>Education</MenuItem>
                    <MenuItem value={"Income"}>Income</MenuItem>
                    <MenuItem value={"Industry"}>Industry</MenuItem>
                    <MenuItem value={"Race"}>Race</MenuItem>
                    <MenuItem value={"Transportation"}>
                      Transport Medium
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            {CRIME_DROPDOWN.includes(state?.selectedTab) &&
              state?.filters?.city?.length > 0 && (
                <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                  <InputLabel id="demo-simple-select-label" shrink>
                    Select value as
                  </InputLabel>
                  <Select
                    value={state?.filters?.crimeType}
                    label="Select value as"
                    onChange={handleCrimeTypeChange}
                  >
                    <MenuItem value={"Property crime"}>Property Crime</MenuItem>
                    <MenuItem value={"Violent crime"}>Violent Crime</MenuItem>
                  </Select>
                </FormControl>
              )}
            {ZIPCODE_DROPDOWN.includes(state?.selectedTab) && (
              <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                <InputLabel id="demo-multiple-checkbox-label" shrink>
                  Select Zipcode
                </InputLabel>
                <Select
                  size="small"
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  value={state?.filters?.zipcode}
                  onChange={handleZipCodeChange}
                  label="Select Zipcode"
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value: any) => (
                        <Chip
                          key={value}
                          label={value}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          onDelete={() => handleZipcodeDelete(value)}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {zipCodes.map((name) => (
                    <MenuItem key={name} value={name}>
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {state.filters.predictionsArray.length === 0 &&
              VALUE_DROPDOWN.includes(state?.selectedTab) &&
              state?.filters?.city?.length > 0 && (
                <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                  <InputLabel id="demo-simple-select-label" shrink>
                    Select value as
                  </InputLabel>
                  <Select
                    value={state?.filters?.value}
                    label="Select value as"
                    onChange={handleValueChange}
                  >
                    <MenuItem value={"trend"}>Long Term Trend</MenuItem>
                    <MenuItem value={"growth"}>
                      Growth (Month on Month)
                    </MenuItem>
                    {PERCENTAGE_DROPDOWN.includes(state?.selectedTab) && (
                      <MenuItem value={"percentage"}>Percentage</MenuItem>
                    )}
                    <MenuItem value={"Absolute"}>Absolute</MenuItem>
                  </Select>
                </FormControl>
              )}
            {QUARTER_DROPDOWN.includes(state?.selectedTab) && (
              <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                <InputLabel id="demo-multiple-checkbox-label">
                  Select the time period{" "}
                </InputLabel>
                <Select
                  size="small"
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  value={state?.filters?.quarterPredictionFor || qYears[0]}
                  onChange={handleQuarterPredictionChange}
                  label="Select the time period"
                  MenuProps={MenuProps}
                >
                  {qYears.map((name: any) => (
                    <MenuItem key={name} value={name}>
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
                  {RENTAL_DROPDOWN.includes(state?.selectedTab) &&
              state?.filters?.city?.length > 0 && (
                <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                  <InputLabel id="demo-simple-select-label" shrink>
                    Select value as
                  </InputLabel>
                  <Select
                    value={state?.filters?.rentalValue}
                    label="Select value as"
                    onChange={handleRentalValueChange}
                  >
                 <MenuItem value={"all_home_median_rental_price"}>All Home Median Rental Price</MenuItem>
                    <MenuItem value={"one_bedroom_rental_price"}>1 Bedroom Rental</MenuItem>
                    <MenuItem value={"two_bedroom_rental_price"}>2 Bedroom Rental</MenuItem>
                    <MenuItem value={"three_bedroom_rental_price"}>3 Bedroom Rental</MenuItem>
                    <MenuItem value={"four_bedroom_rental_price"}>4 Bedroom Rental</MenuItem>
                    <MenuItem value={"five_or_more_bedroom_rental_price"}>5 or More Bedroom Rental</MenuItem>
                 
                  </Select>
                </FormControl>
              )}
            {SHOW_INTERVALS.includes(state?.selectedTab) &&
              state?.filters?.city?.length < 2 &&
              state?.filters.value !== "Per" &&
              state?.filters?.zipcode?.length < 2 && (
                <FormControl sx={{ m: 1, width: 300 }}>
                  <RadioGroup>
                    <FormControlLabel
                      className="radio-text"
                      control={
                        <Checkbox
                          checked={state?.filters?.showInterval}
                          style={{ color: "#fff" }}
                        />
                      }
                      label="Show Intervals"
                      onChange={() => handleIntervalChange()}
                    />
                  </RadioGroup>
                </FormControl>
              )}
            {ADJUSTED_PREDICTIONS.includes(state?.selectedTab) &&
              state?.filters?.city?.length < 2 &&
              state?.filters?.zipcode?.length < 1 &&
              !state?.filters?.showInterval && (
                <FormControl sx={{ m: 1, width: 300 }}>
                  <InputLabel
                    id="demo-simple-select-label"
                    style={{
                      marginTop: "-6px",
                      fontSize: "20px",
                      fontWeight: "500",
                      marginLeft: "-10px",
                    }}
                    shrink
                  >
                    Adjusted Predictions To
                  </InputLabel>
                  <RadioGroup
                    style={{
                      background: "#171d25",
                      padding: "10px",
                      margin: "12px 2px 2px 2px",
                      border: "1px solid white",
                    }}
                  >
                    <div>
                      <FormControlLabel
                        className="radio-text"
                        control={
                          <Checkbox
                            checked={state?.filters?.adjustedPredictions}
                            style={{ color: "#fff" }}
                          />
                        }
                        label=" Mortgage Rates"
                        onChange={() =>
                          handlePredictionChangeAutocomplete(
                            "Mortgage Rates",
                            state?.filters?.adjustedPredictions
                          )
                        }
                      />
                      {state?.filters?.predictionsArray.includes(
                        "Mortgage Rates"
                      ) && (
                        <span
                          style={{
                            fontSize: "13px",
                            marginLeft: "34%",
                            marginTop: "2%",
                          }}
                        >
                          <InfoOutlinedIcon
                            className="what-if-text-icon"
                            onClick={(e) => {
                              setAdjustedRateFor("Mortgage Rates");
                              handleOpenAdjustedPredictionPopup(true);
                            }}
                          />
                        </span>
                      )}
                    </div>
                    <div>
                      <FormControlLabel
                        className="radio-text"
                        control={
                          <Checkbox
                            checked={
                              state?.filters?.adjustedSentimentsByConsumer
                            }
                            style={{ color: "#fff" }}
                          />
                        }
                        label="Consumer Sentiments(HPSI)"
                        onChange={() =>
                          handlePredictionChangeAutocomplete(
                            "Consumer Sentiments(HPSI)",
                            state?.filters?.adjustedSentimentsByConsumer
                          )
                        }
                      />
                      {state?.filters?.predictionsArray.includes(
                        "Consumer Sentiments(HPSI)"
                      ) && (
                        <span
                          style={{
                            fontSize: "13px",
                            marginLeft: "6%",
                            marginTop: "2%",
                          }}
                        >
                          <InfoOutlinedIcon
                            className="what-if-text-icon"
                            onClick={(e) => {
                              setAdjustedRateFor("Consumer Sentiments(HPSI)");
                              handleOpenAdjustedSentimentPopup(true);
                            }}
                          />
                        </span>
                      )}
                    </div>
                    <div>
                      <FormControlLabel
                        className="radio-text"
                        control={
                          <Checkbox
                            checked={
                              state?.filters?.adjustedSentimentsByBuilder
                            }
                            style={{ color: "#fff" }}
                          />
                        }
                        label="Builder Sentiments(NAHB)"
                        onChange={() =>
                          handlePredictionChangeAutocomplete(
                            "Builder Sentiments(NAHB)",
                            state?.filters?.adjustedSentimentsByBuilder
                          )
                        }
                      />

                      {state?.filters?.predictionsArray.includes(
                        "Builder Sentiments(NAHB)"
                      ) && (
                        <span
                          style={{
                            fontSize: "13px",
                            marginLeft: "11%",
                            marginTop: "2%",
                          }}
                        >
                          <InfoOutlinedIcon
                            className="what-if-text-icon"
                            onClick={(e) => {
                              setAdjustedRateFor("Builder Sentiments(NAHB)");
                              handleOpenAdjustedSentimentPopup(true);
                            }}
                          />
                        </span>
                      )}
                    </div>
                  </RadioGroup>
                  {/* <span style={{fontSize: '13px', marginLeft: '20%'}}>Click <InfoOutlinedIcon
                    className='what-if-text-icon'
                    onClick={(e) => handleOpenAdjustedPredictionPopup(true)}
                  /> to change Mortgage Rate</span> */}
                </FormControl>
              )}
            {state.filters.predictionsArray.length === 0 &&
              SLIDER_YEAR_RANGE.includes(state?.selectedTab) &&
              state?.filters.value !== "Per" && (
                <FormControl sx={{ m: 1, width: 300 }}>
                  <Typography gutterBottom>Choose range in years</Typography>
                  <br></br>
                  <IOSSlider
                    valueLabelFormat={valueLabelFormat}
                    getAriaValueText={valuetext}
                    min={SLIDER_START_YEAR}
                    max={SLIDER_END_YEAR}
                    value={year}
                    step={1}
                    onChange={handleYearChange}
                    valueLabelDisplay="on"
                    // marks={years}
                  />
                </FormControl>
              )}
            {!state.filters.submitAdjustedPrediction &&
              SLIDER_COUNT_RANGE.includes(state?.selectedTab) &&
              state?.filters.value !== "Per" && (
                <FormControl sx={{ m: 1, width: 300 }}>
                  <Typography gutterBottom>
                    Select number of cities for prediction <br />
                    summary
                  </Typography>
                  <br></br>
                  <IOSSlider
                    value={state?.filters?.noOfPrediction}
                    max={predictionCount}
                    onChange={handleTopPredictionChange}
                    valueLabelDisplay="on"
                  />
                </FormControl>
              )}
          </>
        )}
        {state?.filters?.selectedType === "countyPredictedMetrics" && (
                  <>
                    <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                      <InputLabel
                        id="demo-multiple-checkbox-label"
                        className="custom-auto-complete-label"
                        shrink
                      >
                        Select County
                      </InputLabel>
                      {DISABLE_MULTI_SELECT.includes(state?.selectedTab) ? (
                        <Autocomplete
                          className="custom-auto-select"
                          key="CountypredictedMetrics-options"
                          id="prediction-metric-tags"
                          onChange={(e, v) => handleSingleCountyChangeAutocomplete(v)}
                          options={county.map((c: any) => c.name)}
                          defaultValue={
                            state?.filters?.county.length > 0
                              ? state?.filters?.county[0]
                              : state?.filters?.county
                          }
                          value={
                            state?.filters?.county.length > 0
                              ? state?.filters?.county[0]
                              : state?.filters?.county
                          }
                          renderInput={(params) => (
                            <TextField {...params} placeholder="City" />
                          )}
                        />
                      ) : (
                        <Autocomplete
                          multiple
                          className="custom-auto-select"
                          key="CountypredictedMetrics-options"
                          id="prediction-metric-tags"
                          onChange={(e, v) => handleCountyChangeAutocomplete(v)}
                          options={county.map((c: any) => c.name)}
                          defaultValue={state?.filters?.county}
                          value={state?.filters?.county}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="County" />
                          )}
                          renderTags={() => (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {state?.filters?.county.map((value: any, index: any) => (
                                <Chip
                                  key={`county-${value}-${index}`}
                                  label={value}
                                  color="primary"
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                  }}
                                  onDelete={() => handleCountyDelete(value)}
                                />
                              ))}
                            </Box>
                          )}
                        />
                      )}
                    </FormControl>
                    {DEMOGRAPHIC_DROPDOWN.includes(state?.selectedTab) &&
                      state?.filters?.city?.length > 0 && (
                        <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                          <InputLabel id="demo-simple-select-label" shrink>
                            Select value as
                          </InputLabel>
                          <Select
                            value={state?.filters?.demographicValue}
                            label="Select value as"
                            onChange={handleDemographicValueChange}
                          >
                            <MenuItem value={"Age"}>Age</MenuItem>
                            <MenuItem value={"Education"}>Education</MenuItem>
                            <MenuItem value={"Income"}>Income</MenuItem>
                            <MenuItem value={"Industry"}>Industry</MenuItem>
                            <MenuItem value={"Race"}>Race</MenuItem>
                            <MenuItem value={"Transportation"}>
                              Transport Medium
                            </MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    {RENTAL_DROPDOWN.includes(state?.selectedTab) &&
                      state?.filters?.city?.length > 0 && (
                        <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                          <InputLabel id="demo-simple-select-label" shrink>
                            Select value as
                          </InputLabel>
                          <Select
                            value={state?.filters?.rentalValue}
                            label="Select value as"
                            onChange={handleRentalValueChange}
                          >
                            <MenuItem value={"all_home_median_rental_price"}>
                              All Home Median Rental Price
                            </MenuItem>
                            <MenuItem value={"one_bedroom_rental_price"}>
                              1 Bedroom Rental
                            </MenuItem>
                            <MenuItem value={"two_bedroom_rental_price"}>
                              2 Bedroom Rental
                            </MenuItem>
                            <MenuItem value={"three_bedroom_rental_price"}>
                              3 Bedroom Rental
                            </MenuItem>
                            <MenuItem value={"four_bedroom_rental_price"}>
                              4 Bedroom Rental
                            </MenuItem>
                            <MenuItem value={"five_or_more_bedroom_rental_price"}>
                              5 or More Bedroom Rental
                            </MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    {CRIME_DROPDOWN.includes(state?.selectedTab) &&
                      state?.filters?.city?.length > 0 && (
                        <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                          <InputLabel id="demo-simple-select-label" shrink>
                            Select value as
                          </InputLabel>
                          <Select
                            value={state?.filters?.crimeType}
                            label="Select value as"
                            onChange={handleCrimeTypeChange}
                          >
                            <MenuItem value={"Property crime"}>Property Crime</MenuItem>
                            <MenuItem value={"Violent crime"}>Violent Crime</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    {state.filters.predictionsArray.length === 0 &&
                      VALUE_DROPDOWN.includes(state?.selectedTab) &&
                      state?.filters?.county?.length > 0 && (
                        <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                          <InputLabel id="demo-simple-select-label" shrink>
                            Select value as
                          </InputLabel>
                          <Select
                            value={state?.filters?.value}
                            label="Select value as"
                            onChange={handleValueChange}
                          >
                            <MenuItem value={"trend"}>Long Term Trend</MenuItem>
                            <MenuItem value={"growth"}>
                              Growth (Month on Month)
                            </MenuItem>
                            {/* {PERCENTAGE_DROPDOWN.includes(state?.selectedTab) && (
                              <MenuItem value={"percentage"}>Percentage</MenuItem>
                            )} */}
                            <MenuItem value={"Absolute"}>Absolute</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    {QUARTER_DROPDOWN.includes(state?.selectedTab) && (
                      <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                        <InputLabel id="demo-multiple-checkbox-label">
                          Select the time period{" "}
                        </InputLabel>
                        <Select
                          size="small"
                          labelId="demo-multiple-checkbox-label"
                          id="demo-multiple-checkbox"
                          value={state?.filters?.quarterPredictionFor || qYears[0]}
                          onChange={handleQuarterPredictionChange}
                          label="Select the time period"
                          MenuProps={MenuProps}
                        >
                          {qYears.map((name: any) => (
                            <MenuItem key={name} value={name}>
                              <ListItemText primary={name} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
        
                    {state.filters.predictionsArray.length === 0 &&
                      SLIDER_YEAR_RANGE.includes(state?.selectedTab) &&
                      state?.filters.value !== "percentage" && (
                        <FormControl sx={{ m: 1, width: 300 }}>
                          <Typography gutterBottom>Choose range in years</Typography>
                          <br></br>
                          <IOSSlider
                            valueLabelFormat={valueLabelFormat}
                            getAriaValueText={valuetext}
                            min={SLIDER_START_YEAR}
                            max={SLIDER_END_YEAR}
                            value={year}
                            step={1}
                            onChange={handleYearChange}
                            valueLabelDisplay="on"
                            // marks={years}
                          />
                        </FormControl>
                      )}
        
                    {!state.filters.submitAdjustedPrediction &&
                      SLIDER_COUNT_RANGE.includes(state?.selectedTab) &&
                      state?.filters.value !== "percentage" && (
                        <FormControl sx={{ m: 1, width: 300 }}>
                          <Typography gutterBottom>
                            Select number of cities for prediction <br />
                            summary
                          </Typography>
                          <br></br>
                          <IOSSlider
                            value={state?.filters?.noOfPrediction}
                            max={predictionCount}
                            onChange={handleTopPredictionChange}
                            valueLabelDisplay="on"
                          />
                        </FormControl>
                      )}
                  </>
                )}
        {state?.filters?.selectedType === "msaPredictedMetrics" && (
          <>
            <FormControl sx={{ m: 1, width: 300 }} variant="standard">
              <InputLabel
                id="demo-multiple-checkbox-label"
                className="custom-auto-complete-label"
                shrink
              >
                Select MSA
              </InputLabel>
              {DISABLE_MSA_MULTI_SELECT.includes(state?.selectedTab) ? (
                <Autocomplete
                  className="custom-auto-select"
                  key="predictedMetrics-options"
                  id="prediction-metric-msa-single-select"
                  onChange={(e, v) => handleSingleMSAChangeAutocomplete(v)}
                  options={state?.filters?.msaInfo}
                  defaultValue={
                    state?.filters?.msa.length > 0
                      ? state?.filters?.msa[0]
                      : state?.filters?.msa
                  }
                  value={
                    state?.filters?.msa.length > 0
                      ? state?.filters?.msa[0]
                      : state?.filters?.msa
                  }
                  getOptionLabel={(option: any) =>
                    `${option?.alias} ${option?.symbol}`
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder="MSA" />
                  )}
                />
              ) : (
                <Autocomplete
                  multiple
                  className="custom-auto-select"
                  key="msaPredictedMetrics-options"
                  id="prediction-metric-tags"
                  onChange={(e, v) => handleMSAChangeAutocomplete(v)}
                  options={state?.filters?.msaInfo}
                  value={state?.filters?.msa}
                  getOptionLabel={(option: any) =>
                    `${option?.alias} ${option?.symbol}`
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder="MSA" />
                  )}
                  renderTags={() => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {state?.filters?.msa.map((value: any) => (
                        <Chip
                          key={value.name}
                          label={`${value?.alias} ${value?.symbol}`}
                          color="primary"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          onDelete={() => handleMSADelete(value)}
                        />
                      ))}
                    </Box>
                  )}
                />
              )}
            </FormControl>
          </>
        )}
        {state?.filters?.selectedType === "msaPredictedMetrics" && (
          <>
            {state.filters.predictionsArray.length === 0 &&
              VALUE_DROPDOWN.includes(state?.selectedTab) &&
              state?.filters?.city?.length > 0 && (
                <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                  <InputLabel id="demo-simple-select-label" shrink>
                    Select value as
                  </InputLabel>
                  <Select
                    value={state?.filters?.value}
                    label="Select value as"
                    onChange={handleValueChange}
                  >
                    <MenuItem value={"trend"}>Long Term Trend</MenuItem>
                    <MenuItem value={"growth"}>
                      Growth (Month on Month)
                    </MenuItem>
                    {PERCENTAGE_DROPDOWN.includes(state?.selectedTab) && (
                      <MenuItem value={"percentage"}>Percentage</MenuItem>
                    )}
                    <MenuItem value={"Absolute"}>Absolute</MenuItem>
                  </Select>
                </FormControl>
              )}
                {RENTAL_DROPDOWN.includes(state?.selectedTab) &&
              state?.filters?.city?.length > 0 && (
                <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                  <InputLabel id="demo-simple-select-label" shrink>
                    Select value as
                  </InputLabel>
                  <Select
                    value={state?.filters?.rentalValue}
                    label="Select value as"
                    onChange={handleRentalValueChange}
                  >
                    <MenuItem value={"all_home_median_rental_price"}>All Home Median Rental Price</MenuItem>
                    <MenuItem value={"single_family_rental_price"}>Single Family Rental Price</MenuItem>
                    <MenuItem value={"multi_family_rental_price"}>Multi Family Rental Price</MenuItem>
                 
                  </Select>
                </FormControl>
              )}
            {ADJUSTED_PREDICTIONS.includes(state?.selectedTab) &&
              state?.filters?.msa?.length < 2 &&
              state?.filters?.zipcode?.length < 2 &&
              !state?.filters?.showInterval &&  state.filters.homeSalesArray.length === 1 && state.filters.homeSalesArray.includes('total_sales') && (
                <FormControl sx={{ m: 1, width: 300 }}>
                  <InputLabel
                    id="demo-simple-select-label"
                    style={{
                      marginTop: "-6px",
                      fontSize: "20px",
                      fontWeight: "500",
                      marginLeft: "-10px",
                    }}
                    shrink
                  >
                    Adjusted Predictions To
                  </InputLabel>
                  <RadioGroup
                    style={{
                      background: "#171d25",
                      padding: "10px",
                      margin: "12px 2px 2px 2px",
                      border: "1px solid white",
                    }}
                  >
                    <div>
                      <FormControlLabel
                        className="radio-text"
                        control={
                          <Checkbox
                            checked={state?.filters?.adjustedPredictions}
                            style={{ color: "#fff" }}
                          />
                        }
                        label=" Mortgage Rates"
                        onChange={() =>
                          handlePredictionChangeAutocomplete(
                            "Mortgage Rates",
                            state?.filters?.adjustedPredictions
                          )
                        }
                      />
                      {state?.filters?.predictionsArray.includes(
                        "Mortgage Rates"
                      ) && (
                        <span
                          style={{
                            fontSize: "13px",
                            marginLeft: "33%",
                            marginTop: "2%",
                          }}
                        >
                          <InfoOutlinedIcon
                            className="what-if-text-icon"
                            onClick={(e) => {
                              setAdjustedRateFor("Mortgage Rates");
                              handleOpenAdjustedPredictionPopup(true);
                            }}
                          />
                        </span>
                      )}
                    </div>
                    <div>
                      <FormControlLabel
                        className="radio-text"
                        control={
                          <Checkbox
                            checked={
                              state?.filters?.adjustedSentimentsByConsumer
                            }
                            style={{ color: "#fff" }}
                          />
                        }
                        label="Consumer Sentiments(HPSI)"
                        onChange={() =>
                          handlePredictionChangeAutocomplete(
                            "Consumer Sentiments(HPSI)",
                            state?.filters?.adjustedSentimentsByConsumer
                          )
                        }
                      />
                      {state?.filters?.predictionsArray.includes(
                        "Consumer Sentiments(HPSI)"
                      ) && (
                        <span
                          style={{
                            fontSize: "13px",
                            marginLeft: "5%",
                            marginTop: "2%",
                          }}
                        >
                          <InfoOutlinedIcon
                            className="what-if-text-icon"
                            onClick={(e) => {
                              setAdjustedRateFor("Consumer Sentiments(HPSI)");
                              handleOpenAdjustedSentimentPopup(true);
                            }}
                          />
                        </span>
                      )}
                    </div>
                    <div>
                      <FormControlLabel
                        className="radio-text"
                        control={
                          <Checkbox
                            checked={
                              state?.filters?.adjustedSentimentsByBuilder
                            }
                            style={{ color: "#fff" }}
                          />
                        }
                        label="Builder Sentiments(NAHB)"
                        onChange={() =>
                          handlePredictionChangeAutocomplete(
                            "Builder Sentiments(NAHB)",
                            state?.filters?.adjustedSentimentsByBuilder
                          )
                        }
                      />

                      {state?.filters?.predictionsArray.includes(
                        "Builder Sentiments(NAHB)"
                      ) && (
                        <span
                          style={{
                            fontSize: "13px",
                            marginLeft: "10%",
                            marginTop: "2%",
                          }}
                        >
                          <InfoOutlinedIcon
                            className="what-if-text-icon"
                            onClick={(e) => {
                              setAdjustedRateFor("Builder Sentiments(NAHB)");
                              handleOpenAdjustedSentimentPopup(true);
                            }}
                          />
                        </span>
                      )}
                    </div>
                  </RadioGroup>
                  {/* <span style={{fontSize: '13px', marginLeft: '20%'}}>Click <InfoOutlinedIcon
                 className='what-if-text-icon'
                 onClick={(e) => handleOpenAdjustedPredictionPopup(true)}
               /> to change Mortgage Rate</span> */}
                </FormControl>
              )}
               {state?.selectedTab==='Home sales' &&
              !state?.filters?.showInterval && (
                <FormControl sx={{ m: 1, width: 300 }}>
                  <InputLabel
                    id="demo-simple-select-label"
                    style={{
                      marginTop: "-2px",
                      fontSize: "20px",
                      fontWeight: "500",
                      marginLeft: "-10px",
                    }}
                    shrink
                  >
                    Compare Home Sales To
                  </InputLabel>
                  <RadioGroup
                    style={{
                      background: "#171d25",
                      padding: "10px",
                      margin: "12px 2px 2px 2px",
                      border: "1px solid white",
                    }}
                  >
                    <div>
                      <FormControlLabel
                        className="radio-text"
                        control={
                          <Checkbox
                            checked={state?.filters?.totalSales}
                            style={{ color: dataAvailability.totalSales ? "#fff" : "#555" }}
                            disabled={!dataAvailability.totalSales}
                            sx={{
                              // Target the SVG directly with higher specificity
                              '& .MuiSvgIcon-root': {
                                fill: dataAvailability.totalSales ? '#f7b047' : '#555',
                              },
                              '&.Mui-disabled .MuiSvgIcon-root': {
                                fill: '#555 !important',
                              },
                              '&.Mui-disabled.Mui-checked .MuiSvgIcon-root': {
                                fill: '#555 !important',
                              }
                            }}
                          />
                        }
                        label={
                          <span style={{ 
                            color: dataAvailability.totalSales ? "#fff" : "#555" 
                          }}>
                            Total Sales
                          </span>
                        }
                        onChange={() =>
                          handleHomeSalesChange(
                            "total_sales",
                            state?.filters?.totalSales
                          )
                        }
                      />
                    </div>
                    <div>
                      <FormControlLabel
                        className="radio-text"
                        control={
                          <Checkbox
                            checked={
                              state?.filters?.reSales
                            }
                            style={{ fill: dataAvailability.reSales ? "#fff" : "#555" }}
                            disabled={!dataAvailability.reSales}
                            sx={{
                              // Target the SVG directly with higher specificity
                              '& .MuiSvgIcon-root': {
                                fill: dataAvailability.totalSales ? '#f7b047' : '#555',
                              },
                              '&.Mui-disabled .MuiSvgIcon-root': {
                                fill: '#555 !important',
                              },
                              '&.Mui-disabled.Mui-checked .MuiSvgIcon-root': {
                                fill: '#555 !important',
                              }
                            }}
                          />
                        }
                        label={
                          <span style={{ 
                            color: dataAvailability.reSales ? "#fff" : "#555" 
                          }}>
                            Resale
                          </span>
                        }
                        onChange={() =>
                          handleHomeSalesChange(
                            "resale",
                            state?.filters?.reSales
                          )
                        }
                      />
                    </div>
                    <div>
                      <FormControlLabel
                        className="radio-text"
                        control={
                          <Checkbox
                            checked={
                              state?.filters?.newSales
                            }
                            style={{ color: dataAvailability.newSales ? "#fff" : "#555" }}
                            sx={{
                              // Target the SVG directly with higher specificity
                              '& .MuiSvgIcon-root': {
                                fill: dataAvailability.totalSales ? '#f7b047' : '#555',
                              },
                              '&.Mui-disabled .MuiSvgIcon-root': {
                                fill: '#555 !important',
                              },
                              '&.Mui-disabled.Mui-checked .MuiSvgIcon-root': {
                                fill: '#555 !important',
                              }
                            }}
                            disabled={!dataAvailability.newSales}
                          />
                        }
                        label={
                          <span style={{ 
                            color: dataAvailability.newSales ? "#fff" : "#555" 
                          }}>
                            New Sales
                          </span>
                        }
                        onChange={() =>
                          handleHomeSalesChange(
                            "new_sale",
                            state?.filters?.newSales
                          )
                        }
                      />
                    </div>
                  </RadioGroup>
                  {/* <span style={{fontSize: '13px', marginLeft: '20%'}}>Click <InfoOutlinedIcon
                 className='what-if-text-icon'
                 onClick={(e) => handleOpenAdjustedPredictionPopup(true)}
               /> to change Mortgage Rate</span> */}
                </FormControl>
              )}
            {(state.filters.predictionsArray.length === 0 ||
              state?.selectedTab === "Months of Supply") &&
              SLIDER_YEAR_RANGE.includes(state?.selectedTab) && (
                <FormControl sx={{ m: 1, width: 300 }}>
                  <Typography gutterBottom>Choose range in years</Typography>
                  <br></br>
                  <IOSSlider
                    valueLabelFormat={valueLabelFormat}
                    getAriaValueText={valuetext}
                    min={SLIDER_START_YEAR}
                    max={SLIDER_END_YEAR}
                    value={year}
                    step={1}
                    onChange={handleYearChange}
                    valueLabelDisplay="on"
                    // marks={years}
                  />
                </FormControl>
              )}
              
          </>
        )}

        {state?.filters?.selectedType === "zipcodePredictedMetrics" && (
          <>
            <FormControl sx={{ m: 1, width: 300 }} variant="standard">
              <InputLabel
                className="custom-auto-complete-label"
                id="demo-multiple-checkbox-label"
                shrink
              >
                Select Zipcode
              </InputLabel>
              {DISABLE_MULTI_SELECT.includes(state?.selectedTab) ? (
                <Autocomplete
                  className="custom-auto-select"
                  key="zipcodePredictedMetrics-options"
                  id="zipcode-prediction-single-select"
                  onChange={(e, v) => handleSingleZipcodeChangeAutocomplete(v)}
                  options={zipCodesPredictions}
                  defaultValue={
                    state?.filters?.zipcodePrediction.length > 0
                      ? state?.filters?.zipcodePrediction[0]
                      : state?.filters?.zipcodePrediction
                  }
                  value={
                    state?.filters?.zipcodePrediction.length > 0
                      ? state?.filters?.zipcodePrediction[0]
                      : state?.filters?.zipcodePrediction
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Zipcode" />
                  )}
                />
              ) : (
                <Autocomplete
                  multiple={!DISABLE_MULTI_SELECT.includes(state?.selectedTab)}
                  id="zipcode-prediction-tags"
                  className="custom-auto-select"
                  key="zipcodePredictedMetrics-options"
                  onChange={(e, v) => handleZipCodePredictionChange(v)}
                  options={zipCodesPredictions}
                  defaultValue={state.filters.zipcodePrediction}
                  value={state.filters.zipcodePrediction}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        state.filters.zipcodePrediction.length ? "" : "Zipcode"
                      }
                    />
                  )}
                  renderTags={() => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {state.filters.zipcodePrediction.map((value: any) => (
                        <Chip
                          key={value}
                          label={value}
                          color="primary"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          onDelete={() => handleZipcodePredictionDelete(value)}
                        />
                      ))}
                    </Box>
                  )}
                />
              )}
            </FormControl>
            {!PROPERTY_TYP_TAB.includes(state?.selectedTab) && (
              <>
                {state.filters.predictionsArray.length === 0 && !RENTAL_DROPDOWN.includes(state?.selectedTab) &&  (
                  <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                    <InputLabel id="demo-simple-select-label" shrink>
                      Select value as
                    </InputLabel>
                    <Select
                      value={state?.filters?.value}
                      label="Select value as"
                      onChange={handleValueChange}
                    >
                      <MenuItem value={"trend"}>Long Term Trend</MenuItem>
                      <MenuItem value={"growth"}>
                        Growth (Month on Month)
                      </MenuItem>
                      {PERCENTAGE_DROPDOWN.includes(state?.selectedTab) && (
                        <MenuItem value={"percentage"}>Percentage</MenuItem>
                      )}
                      <MenuItem value={"Absolute"}>Absolute</MenuItem>
                    </Select>
                  </FormControl>
                )}
                {state?.filters?.city?.length > 0 &&
                  state?.filters.value !== "Per" && (
                    <>
                      {state?.filters?.city?.length < 2 &&
                        state?.filters?.zipcodePrediction?.length < 2 && (
                          <>
                            { !RENTAL_DROPDOWN.includes(state?.selectedTab) && (
                              <FormControl sx={{ m: 1, width: 300 }}>
                              <RadioGroup>
                                <FormControlLabel
                                  className="radio-text"
                                  control={
                                    <Checkbox
                                      checked={state?.filters?.showInterval}
                                      style={{ color: "#fff" }}
                                    />
                                  }
                                  label="Show Intervals"
                                  onChange={() => handleIntervalChange()}
                                />
                              </RadioGroup>
                            </FormControl>
                            )}
                            {ADJUSTED_PREDICTIONS.includes(
                              state?.selectedTab
                            ) && (
                              <FormControl sx={{ m: 1, width: 300 }}>
                                <InputLabel
                                  id="demo-simple-select-label"
                                  style={{
                                    marginTop: "-6px",
                                    fontSize: "20px",
                                    fontWeight: "500",
                                    marginLeft: "-10px",
                                  }}
                                  shrink
                                >
                                  Adjusted Predictions To
                                </InputLabel>
                                <RadioGroup
                                  style={{
                                    background: "#171d25",
                                    padding: "10px",
                                    margin: "12px 2px 2px 2px",
                                    border: "1px solid white",
                                  }}
                                >
                                  <div>
                                    <FormControlLabel
                                      className="radio-text"
                                      control={
                                        <Checkbox
                                          checked={
                                            state?.filters?.adjustedPredictions
                                          }
                                          style={{ color: "#fff" }}
                                        />
                                      }
                                      label=" Mortgage Rates"
                                      onChange={() =>
                                        handlePredictionChangeAutocomplete(
                                          "Mortgage Rates",
                                          state?.filters?.adjustedPredictions
                                        )
                                      }
                                    />
                                    {state?.filters?.predictionsArray.includes(
                                      "Mortgage Rates"
                                    ) && (
                                      <span
                                        style={{
                                          fontSize: "13px",
                                          marginLeft: "33%",
                                          marginTop: "2%",
                                        }}
                                      >
                                        <InfoOutlinedIcon
                                          className="what-if-text-icon"
                                          onClick={(e) => {
                                            setAdjustedRateFor(
                                              "Mortgage Rates"
                                            );
                                            handleOpenAdjustedPredictionPopup(
                                              true
                                            );
                                          }}
                                        />
                                      </span>
                                    )}
                                  </div>
                                  <div>
                                    <FormControlLabel
                                      className="radio-text"
                                      control={
                                        <Checkbox
                                          checked={
                                            state?.filters
                                              ?.adjustedSentimentsByConsumer
                                          }
                                          style={{ color: "#fff" }}
                                        />
                                      }
                                      label="Consumer Sentiments(HPSI)"
                                      onChange={() =>
                                        handlePredictionChangeAutocomplete(
                                          "Consumer Sentiments(HPSI)",
                                          state?.filters
                                            ?.adjustedSentimentsByConsumer
                                        )
                                      }
                                    />
                                    {state?.filters?.predictionsArray.includes(
                                      "Consumer Sentiments(HPSI)"
                                    ) && (
                                      <span
                                        style={{
                                          fontSize: "13px",
                                          marginLeft: "5%",
                                          marginTop: "2%",
                                        }}
                                      >
                                        <InfoOutlinedIcon
                                          className="what-if-text-icon"
                                          onClick={(e) => {
                                            setAdjustedRateFor(
                                              "Consumer Sentiments(HPSI)"
                                            );
                                            handleOpenAdjustedSentimentPopup(
                                              true
                                            );
                                          }}
                                        />
                                      </span>
                                    )}
                                  </div>
                                  <div>
                                    <FormControlLabel
                                      className="radio-text"
                                      control={
                                        <Checkbox
                                          checked={
                                            state?.filters
                                              ?.adjustedSentimentsByBuilder
                                          }
                                          style={{ color: "#fff" }}
                                        />
                                      }
                                      label="Builder Sentiments(NAHB)"
                                      onChange={() =>
                                        handlePredictionChangeAutocomplete(
                                          "Builder Sentiments(NAHB)",
                                          state?.filters
                                            ?.adjustedSentimentsByBuilder
                                        )
                                      }
                                    />

                                    {state?.filters?.predictionsArray.includes(
                                      "Builder Sentiments(NAHB)"
                                    ) && (
                                      <span
                                        style={{
                                          fontSize: "13px",
                                          marginLeft: "10%",
                                          marginTop: "2%",
                                        }}
                                      >
                                        <InfoOutlinedIcon
                                          className="what-if-text-icon"
                                          onClick={(e) => {
                                            setAdjustedRateFor(
                                              "Builder Sentiments(NAHB)"
                                            );
                                            handleOpenAdjustedSentimentPopup(
                                              true
                                            );
                                          }}
                                        />
                                      </span>
                                    )}
                                  </div>
                                </RadioGroup>
                                {/* <span style={{fontSize: '13px', marginLeft: '20%'}}>Click <InfoOutlinedIcon
                    className='what-if-text-icon'
                    onClick={(e) => handleOpenAdjustedPredictionPopup(true)}
                  /> to change Mortgage Rate</span> */}
                              </FormControl>
                              // <FormControl sx={{m: 1, width: 300}} style={{marginTop: '-12px'}}>
                              //   <RadioGroup>
                              //     <FormControlLabel
                              //       className='radio-text'
                              //       control={<Checkbox checked={state?.filters?.adjustedPredictions} style={{color: '#fff'}}/>}
                              //       label='Adjusted Predictions To Mortgage Rates'
                              //       onChange={() => handleAdjustedPredictionChange('Mortgage rates')
                              //       }
                              //     />
                              //   </RadioGroup>
                              //   <span style={{fontSize: '13px', marginLeft: '30%'}}>Click <InfoOutlinedIcon
                              //     className='what-if-text-icon'
                              //     onClick={(e) => handleOpenAdjustedPredictionPopup(true)}
                              //   /> for more Info</span>

                              // </FormControl>
                            )}
                          </>
                        )}
                        <FormControl sx={{ m: 1, width: 300 }}>
                          <Typography gutterBottom>
                            Choose range in years
                          </Typography>
                          <br></br>
                          <IOSSlider
                            valueLabelFormat={valueLabelFormat}
                            getAriaValueText={valuetext}
                            min={SLIDER_START_YEAR}
                            max={SLIDER_END_YEAR}
                            value={year}
                            step={1}
                            onChange={handleYearChange}
                            valueLabelDisplay="on"
                            // marks={years}
                          />
                        </FormControl>
                      
                    </>
                  )}
              </>
            )}
          </>
        )}
        {(state?.filters?.selectedType === "cityPredictions" ||
          state?.filters?.selectedType === "msaPredictions"||
          state?.filters?.selectedType === "countyPredictions")&& (
          <>
            <FormControl sx={{ m: 1, width: 300 }} variant="standard">
              <InputLabel id="demo-multiple-checkbox-label">
                Select the time period{" "}
              </InputLabel>
              <Select
                size="small"
                labelId="demo-multiple-checkbox-label"
                id="demo-multiple-checkbox"
                value={state?.filters?.predictionFor || dates[0]}
                onChange={handlePredictionChange}
                label="Select the time period"
                MenuProps={MenuProps}
              >
                {dates.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ m: 1, width: 300 }}>
              <Typography gutterBottom>
                Select number of cities for prediction <br />
                summary
              </Typography>
              <br></br>
              <IOSSlider
                value={state?.filters?.noOfPrediction}
                max={predictionCount}
                onChange={handleTopPredictionChange}
                valueLabelDisplay="on"
              />
            </FormControl>
          </>
        )}
        {state?.filters?.selectedType === "heatMap" && (
          <>
            <FormControl sx={{ m: 1, width: 300 }} variant="standard">
              <InputLabel shrink id="demo-simple-select-label">
                Select Profitability
              </InputLabel>
              <Select
                value={profitability}
                label="Select Zipcode"
                onChange={handleProfitabilityChange}
                size="small"
              >
                {["All Zipcodes", "Profitable"]?.map(
                  (item: any, index: number) => (
                    <MenuItem value={item} key={index}>
                      {item}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
            <FormControl sx={{ m: 1, width: 300 }} variant="standard">
              <InputLabel shrink id="demo-simple-select-label">
                Select Months
              </InputLabel>
              <Select
                name="heat-map-months"
                value={state?.filters?.heatMapMonths}
                label="Select State"
                onChange={handleHeapMapMonthChange}
                size="small"
              >
                {HEATMAP_MONTH_OPTIONS?.map((info: any) => (
                  <MenuItem value={info} key={info}>
                    {info}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ m: 1, width: 300 }} variant="standard">
              <InputLabel shrink id="demo-simple-select-label">
                Select Distance From Factory Centre
              </InputLabel>
              <Select
                name="heat-map-miles"
                value={state?.filters?.heatMapMiles}
                label="Select Miles"
                onChange={handleHeapMapMilesChange}
                size="small"
              >
                {HEATMAP_MILES_OPTIONS?.map((info: any) => (
                  <MenuItem value={info} key={info}>
                    {info}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {state.user.email.split("@")[1] === "onxhomes.com" && (
              <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="factory_markers"
                        checked={showMarkers}
                        onChange={(e) => handleMarker(e)}
                        style={{ color: "#fff" }}
                      />
                    }
                    label="Show Factory Markers"
                  />
                </FormGroup>
              </FormControl>
            )}

            {state.filters.showSchoolMap && (
              <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                <InputLabel shrink id="demo-simple-select-label">
                  Select School Type
                </InputLabel>
                <Select
                  value={schoolType}
                  label="Select School Tipe"
                  onChange={handleSchoolTypeChange}
                  size="small"
                >
                  {["Public", "Non-Public"]?.map((item: any, index: number) => (
                    <MenuItem value={item} key={index}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {state.filters.showSchoolMap &&
              state.filters.schoolType === "Public" && (
                <FormControl sx={{ m: 1, width: 300 }} variant="standard">
                  <InputLabel shrink id="demo-simple-select-label">
                    Select School Rating
                  </InputLabel>
                  <Select
                    value={schoolRating}
                    label="Select School Tipe"
                    onChange={handleSchoolRatingChange}
                    size="small"
                  >
                    {schoolLevelRating?.map((item: any, index: number) => (
                      <MenuItem value={item.value} key={index}>
                        {item.key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            <FormControl sx={{ m: 1, width: 300 }} variant="standard">
              <Typography gutterBottom>Choose no. of top records</Typography>
              <br />
              <IOSSlider
                aria-label="heatmap slider"
                defaultValue={20}
                value={heatMapRecords}
                max={state?.filters?.totalHeatmapRecords}
                onChangeCommitted={handleHeatMapRecordsChangeCommitted}
                onChange={handleHeatMapRecordsChange}
                valueLabelDisplay="on"
              />
            </FormControl>
            {/* <FormControl sx={{ m: 1, width: 300 }} variant="standard">
              <InputLabel
                shrink
                className="limit-text"
                id="demo-simple-select-label"
              >
                Click{" "}
                <InfoOutlinedIcon
                  className="limit-text-icon"
                  onClick={handleClickOpenDialog}
                />{" "}
                to know more about Heat Map.
              </InputLabel>
              <BootstrapDialog
                onClose={handleCloseDialog}
                aria-labelledby="customized-dialog-title"
                open={openDialog}
              >
                <BootstrapDialogTitle
                  id="customized-dialog-title"
                  onClose={handleCloseDialog}
                >
                  Helpful Tips
                </BootstrapDialogTitle>
                <DialogContent dividers>{returnHeatMapText()}</DialogContent>
              </BootstrapDialog>
            </FormControl> */}
          </>
        )}
        {state?.filters?.selectedType === "zipcodePredictions" && (
          <>
            <FormControl sx={{ m: 1, width: 300 }} variant="standard">
              <InputLabel id="demo-multiple-checkbox-label">
                Select the time period{" "}
              </InputLabel>
              <Select
                size="small"
                labelId="demo-multiple-checkbox-label"
                id="demo-multiple-checkbox"
                value={state?.filters?.zipcodeQuarterPredictionFor || qYears[0]}
                onChange={handleZipcodeQuarterPredictionChange}
                label="Select the time period"
                MenuProps={MenuProps}
              >
                {qYears.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ m: 1, width: 300 }}>
              <Typography gutterBottom>
                Select number of zipcodes for prediction <br />
                summary
              </Typography>
              <br></br>
              <IOSSlider
                value={state?.filters?.noOfPrediction}
                max={predictionCount}
                onChange={handleTopPredictionChange}
                valueLabelDisplay="on"
              />
            </FormControl>
          </>
        )}
      </div>
      <BootstrapDialog
        onClose={handleCloseDialog}
        aria-labelledby="customized-prediction-dialog"
        open={showAdjustedPredictionPopUp}
      >
        <BootstrapDialogTitle
          id="customized-prediction-dialog"
          onClose={handleCloseDialog}
        >
          {getAdjustedText()}
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <Grid container>
            <Grid item xs={12}>
              <span className="what-if-current-heading">
                {adjustedRateFor === "Mortgage Rates"
                  ? "Current Mortgage Rate:"
                  : "Current Sentiment Index:"}{" "}
              </span>
              <span className={"what-if-current-value"}>
              {adjustedRateFor === "Mortgage Rates"
                  ? mortgageRate
                  : adjustedRateFor === "Consumer Sentiments(HPSI)"
                  ? 73
                  : 46}
              </span>
            </Grid>
          </Grid>
          <Box sx={{ flexGrow: 1 }} style={{ marginTop: "20px" }}>
            <Grid container spacing={1} style={{ flexGrow: 1 }}>
              {NO_OF_QUARTERS.map((number) => (
                <>
                  <Grid item xs={0.6}>
                    {showConfirmation ? (
                      <QuarterSlider
                        key={number}
                        id={`quarter_${number}`}
                        valueLabelDisplay={"on"}
                        aria-label={`quarter_${number}`}
                        orientation="vertical"
                        name={`quarter_${number}`}
                        value={confirmationValues[`quarter_${number}`]}
                        marks={marks}
                        step={adjustedRateFor == "Mortgage Rates" ? 0.1 : 0.1}
                        min={0}
                        max={adjustedRateFor == "Mortgage Rates" ? 10 : 100}
                        onChange={sliderChange}
                        disabled
                      />
                    ) : (
                      <QuarterSlider
                        key={number}
                        id={`quarter_${number}`}
                        valueLabelDisplay={adjustedRateFor == "" ? "off" : "on"}
                        aria-label={`quarter_${number}`}
                        orientation="vertical"
                        name={`quarter_${number}`}
                        value={
                          adjustedRateFor == "Mortgage Rates"
                            ? state.filters.adjustedPredictionValues[
                                `quarter_${number}`
                              ]
                            : adjustedRateFor == "Consumer Sentiments(HPSI)"
                            ? state.filters.adjustedSentimentValuesByConsumer[
                                `quarter_${number}`
                              ]
                            : state.filters.adjustedSentimentValuesByBuilder[
                                `quarter_${number}`
                              ] || 0
                        }
                        // value={adjustedPredictionValues[`quarter_${number}`]}
                        marks={marks}
                        step={adjustedRateFor == "Mortgage Rates" ? 0.1 : 0.1}
                        min={0}
                        max={adjustedRateFor == "Mortgage Rates" ? 10 : 100}
                        onChange={sliderChange}
                        disabled={adjustedRateFor == ""}
                      />
                    )}
                    <Typography gutterBottom> Q{number}</Typography>
                  </Grid>
                </>
              ))}
            </Grid>
          </Box>
          <Grid container spacing={2} className="mt1">
            <Grid item xs={8} />
            <Grid item xs={3.2}>
              <FormControl fullWidth variant="standard">
                {showConfirmation ? (
                  <Button
                    className="prediction-submit"
                    size="large"
                    endIcon={<SendIcon />}
                    onClick={handleSubmit}
                    disabled={adjustedRateFor == ""}
                  >
                    Submit
                  </Button>
                ) : (
                  <Button
                    className="prediction-submit"
                    size="large"
                    endIcon={<SendIcon />}
                    onClick={handleConfirmation}
                    disabled={adjustedRateFor == ""}
                  >
                    Confirm
                  </Button>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
      </BootstrapDialog>
    </Drawer>
  );
};

// @ts-ignore
export default SideMenu;
